import { translateRuntimeError } from '../shared/runtimeErrors';
import { runAsyncExport } from './asyncExport';

const JSON_HEADERS = { 'Content-Type': 'application/json' };
// same-origin, but explicit so the HttpOnly session cookie always rides along
const OPTS = { credentials: 'same-origin' };

// --- CSRF --------------------------------------------------------------------
// The backend pairs the HttpOnly session cookie with a READABLE xa_csrf
// cookie and rejects any state-changing /api request whose X-CSRF-Token
// header doesn't echo it (double-submit; see backend main._csrf_protect).
// A cross-site page can make the browser send our cookies but can't read
// this one, so it can never build the header. Read it fresh per request -
// login/logout rotate it.
export function csrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)xa_csrf=([^;]*)/);
  return m ? decodeURIComponent(m[1]) : '';
}

// Fired on a 401 from any non-auth /api endpoint (see apiFetch below) -
// App.jsx listens for this to clear the session and return to the login
// screen with an explanation, instead of leaving a fully-rendered app up
// that toasts "Authentication required" on every subsequent action with no
// way back to sign-in short of a manual reload.
export const SESSION_EXPIRED_EVENT = 'kesher:session-expired';

// Every call in this module goes through here instead of bare fetch(), so a
// new endpoint can't forget the token: unsafe methods get the header
// attached automatically, GETs pass through untouched. Only same-origin
// /api calls belong here - direct-to-GCS uploads (uploadFile) keep using
// bare fetch(): the token would leak to another origin and mean nothing there.
export function apiFetch(url, opts = {}) {
  const method = (opts.method || 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') {
    opts = { ...opts, headers: { ...(opts.headers || {}), 'X-CSRF-Token': csrfToken() } };
  }
  // `background: true` marks a request nobody asked for - a poll on a timer.
  // Not a fetch option, so it's peeled off before the RequestInit is built.
  const { background = false, ...init } = opts;
  return fetch(url, { ...OPTS, ...init })
    .then((r) => {
      // /api/auth/* itself returning 401 is an ordinary "wrong password" /
      // "no session yet" response, not a live session expiring - excluded so
      // a failed login attempt doesn't get mistaken for one.
      if (r.status === 401 && !url.startsWith('/api/auth/')) {
        // Whether anyone was waiting on this decides what the app may do
        // about it. A 401 on something the person just clicked can bounce
        // them to the login screen; a 401 on a 30-second notification poll
        // must not, because they are probably mid-sentence in a form.
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { background } }));
      }
      return r;
    })
    .catch((e) => {
      // A dropped connection rejects fetch() with a raw TypeError ("Failed to
      // fetch" / "NetworkError when attempting to fetch resource") - that
      // browser-internal string used to go straight into a toast via
      // ToastContext's pass-through. Normalised once here so every caller's
      // existing `catch (e) { toast.error(e) }` gets the friendlier message
      // for free.
      if (e instanceof TypeError) throw new Error('You look offline — check your connection and try again.');
      throw e;
    });
}

// Every failed request gets the backend's own X-Correlation-Id (logutil.py's
// install_request_id_hooks mints/echoes one per request) attached to the
// thrown Error, so a toast/error surface can offer "copy diagnostics"
// without the caller having to know anything about how it got there.
export function apiErrorFrom(r, d, fallback) {
  const err = new Error(translateRuntimeError((d && d.error) || fallback));
  err.status = r.status;
  err.body = d; // e.g. { conflict:true, current:{...} } on a 409
  err.correlationId = r.headers.get('X-Correlation-Id') || null;
  return err;
}

async function jsonOrThrow(r, fallback) {
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, fallback);
  return d;
}

// --- auth --------------------------------------------------------------------
export async function getMe() {
  const r = await apiFetch('/api/auth/me', OPTS);
  return jsonOrThrow(r, 'Could not load session'); // { user, memberships?, needs_bootstrap? }
}
export async function login(email, password, orgId) {
  // orgId disambiguates when the same email is registered against more than
  // one org (each is a fully separate account) - omitted on the first try;
  // a 409 { error: 'multiple_accounts', orgs } means the password matched
  // more than one, and the caller should retry with one of those org_ids.
  const body = orgId ? { email, password, org_id: orgId } : { email, password };
  const r = await apiFetch('/api/auth/login', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not sign you in');
}
export async function register(email, password, name) {
  const r = await apiFetch('/api/auth/register', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ email, password, name }) });
  return jsonOrThrow(r, 'Could not create your account');
}
// Public self-service signup (ROADMAP.md §2) - distinct from register() above,
// which is bootstrap/admin-only. Always resolves to { status: 'check_email' }
// on a 202, whether or not the email already has an account anywhere
// (anti-enumeration, same contract as requestPasswordReset below) - no org
// is created yet, that happens in signupVerify() once the link is clicked.
// captchaToken: undefined unless getMe()'s captcha_enabled is true (ROADMAP.md
// §3.1) - the backend's bot_challenge.verify() itself no-ops when no
// provider is configured, so omitting it here is harmless either way.
export async function signup(email, password, name, captchaToken) {
  const r = await apiFetch('/api/auth/signup', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ email, password, name, captcha_token: captchaToken }) });
  return jsonOrThrow(r, 'Could not start signup');
}
// Creates the brand-new org + account together and logs straight in -
// the password-signup counterpart to googleProvision() below.
// fiscalYearStartMonth (1-12, default 1/January): one selection, made once
// at workspace-creation time - see the "Fiscal year start" picker in
// SignupVerifyScreen. tosAccepted: only required once getMe()'s legal.
// tos_version is set (ROADMAP.md §3.1) - omit/false is fine on a deployment
// that hasn't turned that on yet, same "fails open when unconfigured"
// posture the backend takes.
export async function signupVerify(token, orgName, fiscalYearStartMonth = 1, tosAccepted = false, industry = '') {
  const r = await apiFetch('/api/auth/signup/verify', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ token, org_name: orgName, fiscal_year_start_month: fiscalYearStartMonth, tos_accepted: tosAccepted, industry }) });
  return jsonOrThrow(r, 'Could not verify email'); // { user, org }
}
// Non-destructive counterpart to signupVerify above - just the email a
// still-valid token belongs to, so SignupVerifyScreen can show the person
// which account they're about to name a workspace for, before they submit
// (docs/onboarding-ux-design-2026-07.md finding S5). Callers should treat a
// failure here as "don't show an email line", not as a fatal error - the
// real validity check still happens at submit time.
export async function signupVerifyPeek(token) {
  const r = await apiFetch(`/api/auth/signup/verify?token=${encodeURIComponent(token)}`, OPTS);
  return jsonOrThrow(r, 'Invalid or expired verification link'); // { email }
}
export async function logout() {
  await apiFetch('/api/auth/logout', { ...OPTS, method: 'POST' });
}
export async function logoutAll() {
  // "Sign out everywhere": revokes every session server-side, this one included
  const r = await apiFetch('/api/auth/logout_all', { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not sign out everywhere');
}

// --- password reset / email verification / invites ----------------------------
export async function requestPasswordReset(email) {
  // Always 200 whether or not the account exists (anti-enumeration);
  // 503 means email isn't configured on the server at all.
  const r = await apiFetch('/api/auth/request_password_reset', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ email }) });
  return jsonOrThrow(r, 'Could not request a reset');
}
export async function resetPassword(token, new_password) {
  const r = await apiFetch('/api/auth/reset_password', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ token, new_password }) });
  return jsonOrThrow(r, 'Could not reset password'); // err.body.details lists policy problems
}
export async function verifyEmail(token) {
  const r = await apiFetch('/api/auth/verify_email', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ token }) });
  return jsonOrThrow(r, 'Could not verify email');
}
export async function resendVerification() {
  const r = await apiFetch('/api/auth/resend_verification', { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not resend the verification email');
}
// Public counterpart to resendVerification() above - no session required.
// Needed because the hard email-verification login gate (ROADMAP.md §2) means
// an unverified account can't log in to reach the session-gated route.
// Always 200 whether or not the account exists (same anti-enumeration
// contract as requestPasswordReset).
export async function requestEmailVerification(email) {
  const r = await apiFetch('/api/auth/request_email_verification', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ email }) });
  return jsonOrThrow(r, 'Could not request a verification link');
}
export async function acceptInvite(token, password) {
  // Success sets the session cookie - the caller is logged in afterwards
  const r = await apiFetch('/api/auth/accept_invite', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ token, password }) });
  return jsonOrThrow(r, 'Could not accept the invite');
}
// Non-destructive counterpart to acceptInvite above - same shape as
// signupVerifyPeek: AuthLinkScreen calls this on mount so the invite screen
// can say "Joining {org} · {email}" before the person types a password
// (FL-2.2, audit §9). role_label/app_name are present only when the peek
// found exactly one app membership to name - treat their absence as "don't
// show that part", not as a fatal error; the real check is still submit time.
export async function acceptInvitePeek(token) {
  const r = await apiFetch(`/api/auth/accept_invite?token=${encodeURIComponent(token)}`, OPTS);
  return jsonOrThrow(r, 'Invalid or expired invite link'); // { email, org_name, app_name?, role_label? }
}

// --- MFA (ROADMAP.md §2) --------------------------------------------------------
// login()'s 200 response may be { mfa_required: true, ticket } instead of a
// session - verifyMfaLogin completes it. Everything else here acts on the
// caller's OWN account and needs a live session already.
export async function verifyMfaLogin(ticket, code) {
  const r = await apiFetch('/api/auth/mfa/verify_login', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ticket, code }) });
  return jsonOrThrow(r, 'Could not verify the code');
}
export async function mfaEnrollStart() {
  const r = await apiFetch('/api/auth/mfa/enroll/start', { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not start MFA enrollment'); // { secret, otpauth_uri }
}
export async function mfaEnrollConfirm(secret, code) {
  const r = await apiFetch('/api/auth/mfa/enroll/confirm', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ secret, code }) });
  return jsonOrThrow(r, 'That code didn’t match'); // { status, backup_codes }
}
export async function mfaDisable(password) {
  const r = await apiFetch('/api/auth/mfa/disable', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ password }) });
  return jsonOrThrow(r, 'Could not disable MFA');
}
export async function mfaRegenerateBackupCodes(password) {
  const r = await apiFetch('/api/auth/mfa/backup_codes/regenerate', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ password }) });
  return jsonOrThrow(r, 'Could not regenerate backup codes'); // { backup_codes }
}
export async function setNotifyEmail(enabled) {
  const r = await apiFetch('/api/auth/notify-email', { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ enabled }) });
  return jsonOrThrow(r, 'Could not update your notification preference'); // { notify_email }
}

// The Monday "here's what your apps did" email for admins and app owners
// (backend usage/value_digest.py). The account-wide email switch above still
// overrides it.
export async function setWeeklyDigest(enabled) {
  const r = await apiFetch('/api/auth/weekly-digest', { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ enabled }) });
  return jsonOrThrow(r, 'Could not update your weekly summary preference'); // { weekly_digest }
}

// --- platform-wide feedback capture (backend/feedback/routes_feedback.py) -------
export async function submitFeedback(bodyText, screen) {
  const r = await apiFetch('/api/feedback', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ body: bodyText, screen }) });
  return jsonOrThrow(r, 'Could not send feedback'); // { id, created_at }
}
// The submitter's own past feedback, status included - "My feedback".
export async function getMyFeedback() {
  return jsonOrThrow(await apiFetch('/api/feedback/mine', OPTS), 'Could not load your feedback'); // { feedback }
}
// Superuser-only, cross-org - the admin console's Feedback review panel.
// status: 'open' (default) | 'resolved' | 'all'.
export async function listFeedback(status = 'open') {
  return jsonOrThrow(await apiFetch(`/api/admin/feedback?status=${encodeURIComponent(status)}`, OPTS), 'Could not load feedback'); // { feedback }
}
export async function resolveFeedback(id, resolved = true) {
  const r = await apiFetch(`/api/admin/feedback/${id}/resolve`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ resolved }) });
  return jsonOrThrow(r, 'Could not update that feedback');
}

// --- internal admin console v0 (ROADMAP.md §2) ----------------------------------
export async function listTenants() {
  return jsonOrThrow(await apiFetch('/api/admin/tenants', OPTS), 'Could not load tenants'); // { tenants }
}
export async function breakGlass(appId, reason) {
  const r = await apiFetch('/api/admin/break-glass', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ app_id: appId, reason }) });
  return jsonOrThrow(r, 'Could not grant break-glass access'); // { app_id, expires_at, ttl_minutes }
}
// Impersonation (ROADMAP.md §2, admin console closeout) - a read-only
// "view the app as this user" support session, distinct from break-glass
// (which keeps the operator's own superuser identity and only widens
// which app they can reach).
export async function startImpersonation(userId, reason) {
  const r = await apiFetch('/api/admin/impersonate', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ user_id: userId, reason }) });
  return jsonOrThrow(r, 'Could not start impersonation'); // { target_user_id, target_email, target_org_id, expires_at, ttl_minutes }
}
export async function stopImpersonation() {
  const r = await apiFetch('/api/admin/impersonate/stop', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({}) });
  return jsonOrThrow(r, 'Could not stop impersonation'); // { stopped }
}
export async function getImpersonationStatus() {
  return jsonOrThrow(await apiFetch('/api/admin/impersonate/status', OPTS), 'Could not load impersonation status'); // { active, target_user_id?, target_email?, target_org_id?, expires_at? }
}

// Tenant export + hard-delete (Foundation residual #2, ROADMAP.md §2).
// Blob-download pattern mirrors exportAudit() below - fetch+blob keeps the
// HttpOnly session cookie riding along via apiFetch, unlike a bare <a href>.
export async function exportTenant(orgId, format = 'csv') {
  const r = await apiFetch(`/api/admin/tenants/${orgId}/export?format=${format}`, OPTS);
  if (!r.ok) throw apiErrorFrom(r, await r.json().catch(() => ({})), 'Could not export that tenant');
  const blob = await r.blob();
  const match = /filename="([^"]+)"/.exec(r.headers.get('Content-Disposition') || '');
  const filename = match ? match[1] : `tenant-export-${orgId}.zip`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}
export async function hardDeleteTenant(orgId, confirmName) {
  const r = await apiFetch(`/api/admin/tenants/${orgId}/hard_delete`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ confirm: confirmName }) });
  return jsonOrThrow(r, 'Could not delete tenant'); // { org_id, org_name, schema_name, row_counts }
}
// Superuser cross-org override of setFiscalYearStart() above, for support/onboarding.
export async function setTenantFiscalYearStart(orgId, month, mode = 'extend', confirm = false) {
  const r = await apiFetch(`/api/admin/tenants/${orgId}/fiscal_year_start`, { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ month, mode, confirm }) });
  return jsonOrThrow(r, 'Could not update fiscal year start');
}

// --- admin console: billing/caps/lifecycle (ROADMAP.md §3.2 Phase 1) --------
export async function getAdminOrgBilling(orgId) {
  return jsonOrThrow(await apiFetch(`/api/admin/orgs/${orgId}/billing`, OPTS), 'Could not load billing');
}
export async function mutateAdminOrgBilling(orgId, action, reason, fields = {}) {
  const r = await apiFetch(`/api/admin/orgs/${orgId}/billing`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ action, reason, ...fields }) });
  return jsonOrThrow(r, 'Could not update billing');
}
export async function getAdminOrgCaps(orgId) {
  return jsonOrThrow(await apiFetch(`/api/admin/orgs/${orgId}/caps`, OPTS), 'Could not load caps');
}
export async function setAdminOrgCaps(orgId, reason, fields) {
  const r = await apiFetch(`/api/admin/orgs/${orgId}/caps`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ reason, ...fields }) });
  return jsonOrThrow(r, 'Could not update caps');
}
export async function listAdminPlanCaps() {
  return jsonOrThrow(await apiFetch('/api/admin/plan_caps', OPTS), 'Could not load plan caps'); // { plans }
}
export async function setAdminPlanCaps(plan, reason, fields) {
  const r = await apiFetch(`/api/admin/plan_caps/${encodeURIComponent(plan)}`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ reason, ...fields }) });
  return jsonOrThrow(r, 'Could not update plan caps');
}
// `plan` is optional - omitted (or falsy) applies to every org on the
// platform, same as before. Naming a plan scopes the sweep to only orgs
// currently on that plan AND updates that plan's cap default in the same
// call, so a new org signing up onto the plan afterwards inherits the
// same value rather than the sweep being a one-time, existing-orgs-only
// change (backend/admin/admin_console.py's api_admin_bulk_set_caps).
export async function bulkSetAdminOrgCaps(reason, field, value, plan = null) {
  const body = { reason, field, value };
  if (plan) body.plan = plan;
  const r = await apiFetch('/api/admin/orgs/caps/bulk', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not apply the cap to every org');
}
export async function overrideAdminOrgLifecycle(orgId, action, reason, state) {
  const body = { action, reason };
  if (state) body.state = state;
  const r = await apiFetch(`/api/admin/orgs/${orgId}/lifecycle`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not update lifecycle state');
}

// --- admin console: model presets (migration 0072) --------------------------
export async function listAdminModelPresets(includeArchived = false) {
  const qs = includeArchived ? '?include_archived=1' : '';
  return jsonOrThrow(await apiFetch(`/api/admin/model_presets${qs}`, OPTS), 'Could not load model presets'); // { presets }
}
export async function createAdminModelPreset(label, provider, modelId, capabilities = [], costTier = 'medium') {
  const r = await apiFetch('/api/admin/model_presets', {
    ...OPTS, method: 'POST', headers: JSON_HEADERS,
    body: JSON.stringify({ label, provider, model_id: modelId, capabilities, cost_tier: costTier }),
  });
  return jsonOrThrow(r, 'Could not create that model preset');
}
export async function updateAdminModelPreset(id, fields) {
  const r = await apiFetch(`/api/admin/model_presets/${id}`, { ...OPTS, method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(fields) });
  return jsonOrThrow(r, 'Could not update that model preset');
}
export async function setDefaultAdminModelPreset(id) {
  const r = await apiFetch(`/api/admin/model_presets/${id}/set_default`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({}) });
  return jsonOrThrow(r, 'Could not set that as the default model');
}
export async function deleteAdminModelPreset(id) {
  const r = await apiFetch(`/api/admin/model_presets/${id}`, { ...OPTS, method: 'DELETE' });
  return jsonOrThrow(r, 'Could not delete that model preset');
}

// Member/config-access-gated read of the same catalog, for the models
// resource form's picker (ConfigEditor.jsx) - see backend/ai_studio/CLAUDE.md.
export async function listModelPresets() {
  return jsonOrThrow(await apiFetch('/api/ai/model_presets', OPTS), 'Could not load model presets'); // { presets }
}

// --- admin console: alerts + cross-org search (ROADMAP.md §3.2 v1.1) -------
export async function listAdminAlerts() {
  return jsonOrThrow(await apiFetch('/api/admin/alerts', OPTS), 'Could not load alerts'); // { alerts }
}
export async function adminSearch(q) {
  return jsonOrThrow(await apiFetch(`/api/admin/search?q=${encodeURIComponent(q)}`, OPTS), 'Could not search'); // { orgs, apps, users }
}
// db.list_admin_mutations' first HTTP route (migration 0030 added the table
// and function with none) - every billing/caps/lifecycle mutation above,
// plus crypto/audit.py's sensitive_data_accessed rows once a later phase
// wires the decrypt hook that produces them.
export async function listAdminMutations(limit = 20) {
  return jsonOrThrow(
    await apiFetch(`/api/admin/mutations?limit=${limit}`, OPTS),
    'Could not load admin action log'); // { mutations }
}

// --- org identity policy (ROADMAP.md §2) ---------------------------------------
export async function getOrgPolicy() {
  return jsonOrThrow(await apiFetch('/api/org/policy', OPTS), 'Could not load organization policy');
}
export async function setOrgPolicy(patch) {
  const r = await apiFetch('/api/org/policy', { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(patch) });
  return jsonOrThrow(r, 'Could not update organization policy');
}

// --- the org's own encryption key (enterprise-readiness WP-8.6, org admin) ----
// { mode: 'platform'|'operator'|'customer', kek_resource, status: 'platform'|'ok'|'unavailable', grant_principal, grant_role }
export async function getOrgEncryptionKey() {
  return jsonOrThrow(await apiFetch('/api/org/encryption-key', OPTS), 'Could not load the encryption key settings');
}
export async function setOrgEncryptionKey(kms_key_name) {
  const r = await apiFetch('/api/org/encryption-key', { ...OPTS, method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ kms_key_name }) });
  return jsonOrThrow(r, 'Could not use that key');
}
export async function clearOrgEncryptionKey() {
  return jsonOrThrow(await apiFetch('/api/org/encryption-key', { ...OPTS, method: 'DELETE' }), 'Could not go back to the platform key');
}

// --- audit streams to a SIEM (enterprise-readiness WP-5.2, org admin) -------------
// A stream: { id, name, target_url, format, kinds, active, consecutive_failures, next_attempt_at, last_delivered_at, last_error }.
// The credential is write-only; an ndjson stream's signing secret comes back ONCE, as `secret`, from createAuditStream.
export async function listAuditStreams() {
  return jsonOrThrow(await apiFetch('/api/org/audit-streams', OPTS), 'Could not load audit streams');
}
export async function createAuditStream(body) {
  const r = await apiFetch('/api/org/audit-streams', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not create the stream');
}
export async function deleteAuditStream(id) {
  return jsonOrThrow(await apiFetch(`/api/org/audit-streams/${id}`, { ...OPTS, method: 'DELETE' }), 'Could not delete the stream');
}
export async function testAuditStream(id) {
  return jsonOrThrow(await apiFetch(`/api/org/audit-streams/${id}/test`, { ...OPTS, method: 'POST' }), 'Could not send a test event');
}
export async function reactivateAuditStream(id) {
  return jsonOrThrow(await apiFetch(`/api/org/audit-streams/${id}/reactivate`, { ...OPTS, method: 'POST' }), 'Could not reactivate the stream');
}

// --- legal holds, retention rules, data-subject requests (enterprise-readiness WP-6.x, org admin) ---
export async function listLegalHolds() {
  return jsonOrThrow(await apiFetch('/api/org/legal-holds', OPTS), 'Could not load legal holds');
}
export async function createLegalHold(body) {
  const r = await apiFetch('/api/org/legal-holds', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not place the hold');
}
export async function releaseLegalHold(id) {
  return jsonOrThrow(await apiFetch(`/api/org/legal-holds/${id}/release`, { ...OPTS, method: 'POST' }), 'Could not release the hold');
}
export async function listRetentionRules() {
  return jsonOrThrow(await apiFetch('/api/org/retention-rules', OPTS), 'Could not load retention rules');
}
export async function previewRetentionRule(body) {
  const r = await apiFetch('/api/org/retention-rules/preview', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not preview the rule');
}
export async function createRetentionRule(body) {
  const r = await apiFetch('/api/org/retention-rules', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not save the rule');
}
export async function deleteRetentionRule(id) {
  return jsonOrThrow(await apiFetch(`/api/org/retention-rules/${id}`, { ...OPTS, method: 'DELETE' }), 'Could not delete the rule');
}
export async function dsrPreview(body) {
  const r = await apiFetch('/api/org/dsr/preview', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not search for that person');
}
export async function dsrExport(body) {
  const r = await apiFetch('/api/org/dsr/export', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not export that person’s data');
}
export async function dsrErase(body) {
  const r = await apiFetch('/api/org/dsr/erase', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not erase');
}

// --- org display governance (enterprise-polish item 10) -------------------------
// timezone/locale/currency every date/number/money in the live app formats
// through - see core/displayPrefs.js for how the fetched value is applied.
export async function getOrgDisplay() {
  return jsonOrThrow(await apiFetch('/api/org/display', OPTS), 'Could not load display settings');
}
export async function setOrgDisplay(patch) {
  const r = await apiFetch('/api/org/display', { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(patch) });
  return jsonOrThrow(r, 'Could not update display settings');
}

// --- map tile settings (docs/config-vocab-growth-plan-2026-08.md Track B) -----
// Settings-panel reads/writes only — never the plaintext Google key. The
// `map` view itself fetches that separately, per-app, via
// GET /api/apps/<id>/map-tiles (core/map_tiles.py's own docstring on why).
export async function getMapTileSettings() {
  return jsonOrThrow(await apiFetch('/api/org/map-tiles', OPTS), 'Could not load map tile settings');
}
/* Whether the two tokenised public link kinds are available here. Member-
 * readable so a link-issuing screen can say "this is off" without needing
 * admin rights to find out; the PATCH is org admin only. */
/* What this record's computed fields WOULD say, without saving it
 * (backend `appdata/routes_data.py`'s api_preview_row). Debounced by the
 * caller; the route is rate-limited per actor because a formula may carry a
 * LOOKUP and each of those is a query. */
export async function previewRow(appId, entity, values, id) {
  const r = await apiFetch(`/api/apps/${appId}/preview_row`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS,
    body: JSON.stringify({ entity, values, ...(id ? { id } : {}) }),
  });
  return jsonOrThrow(r, 'Could not work that out');
}

// Whether this workspace's files may be sent to an AI model (backend core/files_to_ai.py).
export async function getFilesToAi() {
  return jsonOrThrow(await apiFetch('/api/org/files-to-ai', OPTS), 'Could not load the files and AI setting');
}

export async function setFilesToAi(allowed) {
  const r = await apiFetch('/api/org/files-to-ai', { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ allowed }) });
  return jsonOrThrow(r, 'That didn’t save — mind trying again?');
}

export async function getPublicLinkSettings() {
  return jsonOrThrow(await apiFetch('/api/org/public-links', OPTS), 'Could not load public link settings');
}
export async function setPublicLinkSettings(patch) {
  const r = await apiFetch('/api/org/public-links', { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(patch) });
  return jsonOrThrow(r, 'Could not change public link settings');
}
export async function setGoogleMapsKey(apiKey) {
  const r = await apiFetch('/api/org/map-tiles/google', { ...OPTS, method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ api_key: apiKey }) });
  return jsonOrThrow(r, 'Could not save Google Maps key');
}
export async function deleteGoogleMapsKey() {
  const r = await apiFetch('/api/org/map-tiles/google', { ...OPTS, method: 'DELETE', headers: JSON_HEADERS });
  return jsonOrThrow(r, 'Could not remove Google Maps key');
}
export async function setCustomMapTileTemplate(template) {
  const r = await apiFetch('/api/org/map-tiles/custom', { ...OPTS, method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ template }) });
  return jsonOrThrow(r, 'Could not save custom tile template');
}

// --- billing + usage (ROADMAP.md §3.1) ----------------------------------------
export async function getBillingStatus() {
  return jsonOrThrow(await apiFetch('/api/billing/status', OPTS), 'Could not load billing status');
}
// The one source of truth for plan pricing (billing.py's PLAN_PRICING) -
// live/ChoosePlanPanel.jsx and auth/BillingModal.jsx both render off this
// instead of hardcoding their own numbers.
export async function getPlans() {
  return jsonOrThrow(await apiFetch('/api/billing/plans', OPTS), 'Could not load plan pricing'); // { plans: [{ id, standard_cents, byok_cents, caps }] }
}
export async function createCheckoutSession(plan, byokIntent = false) {
  const body = byokIntent ? { plan, byok_intent: true } : { plan };
  const r = await apiFetch('/api/billing/checkout', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not start checkout'); // { url }
}
export async function createBillingPortalSession() {
  const r = await apiFetch('/api/billing/portal', { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not open billing portal'); // { url }
}
export async function getUsage(days = 30) {
  return jsonOrThrow(await apiFetch(`/api/usage?days=${days}`, OPTS), 'Could not load usage');
}
// Marks the new plan-selection onboarding step (ChoosePlanPanel) seen for
// this org, so it never shows again - see billing.py's route docstring.
export async function markOnboardingPlanSeen() {
  const r = await apiFetch('/api/billing/onboarding', { ...OPTS, method: 'PATCH' });
  return jsonOrThrow(r, 'Could not save that');
}
// Self-service counterpart to exportTenant() above (that one's superuser/
// cross-org; this is any org admin exporting their OWN org) - the
// customer-reachable half of "export-only" once a lapsed trial freezes
// writes (billing.py's GET /api/billing/export). Same blob-download shape.
export async function exportOwnOrg(format = 'csv') {
  const r = await apiFetch(`/api/billing/export?format=${format}`, OPTS);
  if (!r.ok) throw apiErrorFrom(r, await r.json().catch(() => ({})), 'Could not export your data');
  if (r.headers.get('X-Export-Truncated') === '1') {
    // The in-request ZIP stops at a per-table row cap. Drop it and let a background job build the complete one (no cap), then fetch its link.
    const done = await runAsyncExport(apiFetch, apiErrorFrom, '/api/billing/export-job', { format });
    window.location.assign(done.url);
    return;
  }
  const blob = await r.blob();
  const match = /filename="([^"]+)"/.exec(r.headers.get('Content-Disposition') || '');
  const filename = match ? match[1] : 'export.zip';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// --- org self-service AI controls (org admin) ----------------------------------
// ai_kill_switch: pauses/resumes the org's own AI usage - distinct from the
// superuser-only /api/org/ai_caps above (see usage.py's route docstring).
export async function getAiKillSwitch() {
  return jsonOrThrow(await apiFetch('/api/org/ai_kill_switch', OPTS), 'Could not load AI settings');
}
export async function setAiKillSwitch(aiDisabled) {
  const r = await apiFetch('/api/org/ai_kill_switch', { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ ai_disabled: aiDisabled }) });
  return jsonOrThrow(r, 'Could not update AI settings');
}
// overage: the org's own client-adjustable spend ceiling ABOVE its plan's
// included quota (billing.py's PATCH /api/billing/overage) - the correct
// self-service "spend cap" (max_spend_cents_per_month itself is the plan's
// hard quota, reset on every plan change, and stays superuser-only).
export async function getOverageBudget() {
  return jsonOrThrow(await apiFetch('/api/billing/overage', OPTS), 'Could not load overage budget'); // { caps, billed_this_cycle_cents }
}
export async function setOverageBudget(patch) {
  const r = await apiFetch('/api/billing/overage', { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(patch) });
  return jsonOrThrow(r, 'Could not update overage budget');
}

// --- org profile (ROADMAP.md §2's chat-driven profiling prompt) ---------------
export async function setOrgProfile(patch) {
  const r = await apiFetch('/api/org/profile', { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(patch) });
  return jsonOrThrow(r, 'Could not save your answer');
}

// --- fiscal year start (per-org fiscal calendar convention) ------------------
export async function getFiscalYearStart() {
  return jsonOrThrow(await apiFetch('/api/org/fiscal_year_start', OPTS), 'Could not load fiscal year start');
}
// mode: 'extend' (default, safe - never touches existing periods) or
// 'regenerate' (destructive - requires confirm: true).
export async function setFiscalYearStart(month, mode = 'extend', confirm = false) {
  const r = await apiFetch('/api/org/fiscal_year_start', { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ month, mode, confirm }) });
  return jsonOrThrow(r, 'Could not update fiscal year start');
}

// --- Google SSO (ROADMAP.md §2) -------------------------------------------------
// start/callback are full-page redirects, not fetches - see routes_sso.py.
export function googleSignInUrl() {
  return '/api/auth/google/start';
}
export async function googlePending(ticket) {
  const r = await apiFetch(`/api/auth/google/pending?ticket=${encodeURIComponent(ticket)}`, OPTS);
  return jsonOrThrow(r, 'This sign-in attempt has expired'); // { kind: 'pick_org'|'new_account', email, orgs? }
}
export async function googleFinish(ticket, orgId) {
  const r = await apiFetch('/api/auth/google/finish', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ticket, org_id: orgId }) });
  return jsonOrThrow(r, 'Could not complete sign-in');
}
export async function googleProvision(ticket, orgName, fiscalYearStartMonth = 1, tosAccepted = false, industry = '') {
  const r = await apiFetch('/api/auth/google/provision', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ticket, org_name: orgName, fiscal_year_start_month: fiscalYearStartMonth, tos_accepted: tosAccepted, industry }) });
  return jsonOrThrow(r, 'Could not create your organization');
}

// --- Microsoft SSO -----------------------------------------------------------
// Mirrors the Google block above exactly, one connector-shaped difference:
// see identity/routes_sso.py's own note on why these are kept as separate
// endpoints/functions rather than one provider-parameterized set.
export function microsoftSignInUrl() {
  return '/api/auth/microsoft/start';
}
export async function microsoftPending(ticket) {
  const r = await apiFetch(`/api/auth/microsoft/pending?ticket=${encodeURIComponent(ticket)}`, OPTS);
  return jsonOrThrow(r, 'This sign-in attempt has expired');
}
export async function microsoftFinish(ticket, orgId) {
  const r = await apiFetch('/api/auth/microsoft/finish', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ticket, org_id: orgId }) });
  return jsonOrThrow(r, 'Could not complete sign-in');
}
export async function microsoftProvision(ticket, orgName, fiscalYearStartMonth = 1, tosAccepted = false, industry = '') {
  const r = await apiFetch('/api/auth/microsoft/provision', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ticket, org_name: orgName, fiscal_year_start_month: fiscalYearStartMonth, tos_accepted: tosAccepted, industry }) });
  return jsonOrThrow(r, 'Could not create your organization');
}

// --- users + members (org admin / superuser) --------------------------------
// `appId` resolves which org these operate on (users are scoped per-org, one
// row per org - see backend/db/platform.py's orgs section) - omit only for
// the rare cross-org superuser action, which falls back to the caller's own org.
export async function listUsers(appId) {
  const qs = appId ? `?app_id=${encodeURIComponent(appId)}` : '';
  return jsonOrThrow(await apiFetch(`/api/users${qs}`, OPTS), 'Could not load users');
}
export async function createUser(payload, appId) {
  const body = appId ? { ...payload, app_id: appId } : payload;
  const r = await apiFetch('/api/users', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not create user');
}
export async function inviteUser(payload, appId) {
  // { email, name?, is_superuser? } -> 201 created+emailed, 200 re-sent a
  // pending invite, 409 the account is live, 503 email not configured
  const body = appId ? { ...payload, app_id: appId } : payload;
  const r = await apiFetch('/api/users/invite', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not send the invite');
}
export async function unlockUser(userId) {
  // Clears a persistent lockout (too many consecutive failed passwords).
  const r = await apiFetch(`/api/users/${userId}/unlock`, { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not unlock the account');
}
export async function deactivateUser(userId) {
  // Admin offboarding: refuses login even on the right password, and cuts
  // off any session already in flight - distinct from unlock (a self-healed
  // lockout an admin can always clear).
  const r = await apiFetch(`/api/users/${userId}/deactivate`, { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not deactivate the account');
}
export async function reactivateUser(userId) {
  const r = await apiFetch(`/api/users/${userId}/reactivate`, { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not reactivate the account');
}
export async function listAuthEvents({ event = '', email = '', limit = 100, cursor = '' } = {}) {
  // The org's OWN security log (enterprise-readiness WP-4.1, org admin): sign-ins and failures, resets, invites, policy changes,
  // membership changes, and operator access to this org's data. Newest first, cursor-paged. Never another org's events.
  const qs = new URLSearchParams({ ...(event ? { event } : {}), ...(email ? { email } : {}), ...(cursor ? { cursor } : {}), limit }).toString();
  const r = await apiFetch(`/api/org/security-log?${qs}`, OPTS);
  return jsonOrThrow(r, 'Could not load the security log'); // { events, next_cursor }
}
// Plain GET links (the session cookie authenticates a download): the export and the access report an auditor asks for.
export const securityLogExportUrl = (format = 'csv', event = '') =>
  `/api/org/security-log/export?${new URLSearchParams({ format, ...(event ? { event } : {}) })}`;
export const accessReportUrl = (format = 'csv') => `/api/org/access-report?${new URLSearchParams({ format })}`;
export async function listMembers(appId) {
  return jsonOrThrow(await apiFetch(`/api/apps/${appId}/members`, OPTS), 'Could not load members');
}
// Org-wide admin status - PATCH /api/users/<id>, same route setAiStudioAccess
// uses. Deliberately NOT paired with an is_superuser setter: routes_users.py's
// own guard refuses that unless the caller is already a superuser, so a helper
// here would only ever mint a 403 for the org admins this file's callers are.
export async function setOrgAdmin(userId, isOrgAdmin) {
  const r = await apiFetch(`/api/users/${userId}`, { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ is_org_admin: isOrgAdmin }) });
  return jsonOrThrow(r, 'Could not update org-admin status');
}
export async function setMember(appId, user_id, role) {
  const r = await apiFetch(`/api/apps/${appId}/members`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ user_id, role }) });
  return jsonOrThrow(r, 'Could not update member');
}
export async function setAppOwner(appId, user_id, is_owner) {
  const r = await apiFetch(`/api/apps/${appId}/members/${user_id}/owner`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ is_owner }) });
  return jsonOrThrow(r, 'Could not update app-owner status'); // { status, user_id, is_owner }
}
// migration 0094. `config_tier` is 'editor' | 'approver' | null (revoke) —
// the two config-authority rungs BELOW app owner. Owner is the separate
// column above and outranks both; core/webctx.py's config_authority is what
// resolves the two into one answer.
export async function setAppConfigTier(appId, user_id, config_tier) {
  const r = await apiFetch(`/api/apps/${appId}/members/${user_id}/config_tier`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ config_tier }) });
  return jsonOrThrow(r, 'Could not update config access'); // { status, user_id, config_tier }
}

// --- apps + data -------------------------------------------------------------
export async function listApps() {
  return jsonOrThrow(await apiFetch('/api/apps', OPTS), 'Could not load apps');
}
export async function getApp(id) {
  const r = await apiFetch(`/api/apps/${id}`, OPTS);
  if (r.status === 404) return null;
  return jsonOrThrow(r, 'Could not load app'); // { id, config }
}
// Which entities of the app changed since `since` (an ISO time from a previous answer's `now`): { now, entities: { key: { at, others } } }.
// Names and times only; see shared/useLiveRefresh.js.
export async function getChanges(id, since) {
  const q = since ? `?since=${encodeURIComponent(since)}` : '';
  const r = await apiFetch(`/api/apps/${id}/changes${q}`, OPTS);
  return jsonOrThrow(r, 'Could not check for changes');
}
// Presence heartbeat for an open record: marks the caller as viewing it and answers with the OTHER people who are,
// `{ viewers: [{ user_id, name, editing, caret }] }` (`editing` is the field key someone has focus in, or null; `caret` is { start, end } character offsets in it, for a text field). `leaving` removes the
// mark. See shared/useRecordPresence.js.
export async function postPresence(id, entity, recordId, leaving = false, editing = null, caret = null) {
  const r = await apiFetch(`/api/apps/${id}/presence`, { ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entity, record_id: String(recordId), ...(leaving ? { leaving: true } : {}), ...(editing ? { editing } : {}), ...(editing && caret ? { caret } : {}) }), keepalive: leaving });
  return jsonOrThrow(r, 'Could not update who is viewing');
}
// Who has each of these rows open (the ones a list or board is showing): { viewers: { record_id: [{ user_id, name }] } }.
export async function lookupPresence(id, entity, ids) {
  const r = await apiFetch(`/api/apps/${id}/presence/lookup`, { ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entity, ids }) });
  return jsonOrThrow(r, 'Could not check who is viewing');
}
// What the app's automations are waiting on and what they did lately: { waiting: [...], recent: [...] }. Admin-only (403 otherwise).
export async function fetchAutomationActivity(id) {
  return jsonOrThrow(await apiFetch(`/api/apps/${id}/automations/activity`, OPTS), 'Could not load automation activity');
}
export async function cancelAutomationWait(id, waitId) {
  return jsonOrThrow(await apiFetch(`/api/apps/${id}/automations/waits/${encodeURIComponent(waitId)}/cancel`, { ...OPTS, method: 'POST' }), 'Could not cancel that run');
}
// What each role may do to each of the app's own lists (app admins only),
// for the invite dialog's "Shop staff can ..." line. Best effort: {} when it
// can't be had, and the dialog then just names the role as before.
export async function getRoleAccess(id) {
  const r = await apiFetch(`/api/apps/${id}/role_access`, OPTS);
  if (!r.ok) return {};
  const d = await r.json().catch(() => ({}));
  return d.roles || {};
}
export async function getAccess(id) {
  const r = await apiFetch(`/api/apps/${id}/access`, OPTS);
  if (!r.ok) return { access: {}, is_owner: false }; // fall back to showing nothing-hidden if access can't be resolved
  const d = await r.json().catch(() => ({}));
  return { access: d.access || {}, is_owner: !!d.is_owner, employee_id: d.employee_id ?? null }; // access: { entity: { list, get, create, update, delete } }
}
// `acknowledge_data_impact` is the way past the server's data-impact gate: a
// save that would harm rows you already have comes back 409 with
// { data_impact_ack_required, data_impact: [...] }, and the caller re-sends
// with this set once the person has actually been shown what they'd lose.
// Never set it speculatively — the whole point is that nobody loses data
// without having seen, once, what was about to happen to it.
// `sourceTemplate` is the kesher-templates/ catalog name a BRAND-NEW app is
// being created from (App.jsx's createAppFromTemplate). The server stamps it
// on the INSERT only and derives the paired version hash itself from the
// catalog, so this is a provenance label rather than anything trusted — see
// migration 0090 and docs/primitive-change-and-template-drift-scoping-2026-09.md
// §7 item 4. Omitted by every other caller, which is every save of an app
// that already exists.
export async function saveApp(id, config, expected_updated_at = undefined, { acknowledgeDataImpact = false, sourceTemplate = undefined, optionRenames = undefined, fieldRenames = undefined } = {}) {
  const body = { config, expected_updated_at };
  if (optionRenames?.length) body.option_renames = optionRenames;
  // Storage renames (backend/engine/field_rename.py): the column moves with the save.
  if (fieldRenames?.length) body.field_renames = fieldRenames;
  if (acknowledgeDataImpact) body.acknowledge_data_impact = true;
  if (sourceTemplate) body.source_template = sourceTemplate;
  const r = await apiFetch(`/api/apps/${id}`, { ...OPTS, method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not save'); // 409 -> err.body = { conflict, current_updated_at }
}

// What renaming a field's STORAGE key would do to the draft: `{ config, changes, changed }`, the draft with every reference rewritten
// by the server's own cascade (so the browser holds no second copy of where a key can appear). Writes nothing. Throws with the
// server's reason when the rename is refused (a protected field, a taken key).
export async function previewFieldRename(appId, config, fieldRenames) {
  const r = await apiFetch(`/api/apps/${appId}/field_rename/preview`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ config, field_renames: fieldRenames }) });
  return jsonOrThrow(r, 'Could not check that rename');
}

// A browser crash, reported to the server (backend/core/client_errors.py, WP-26.5), which scrubs it, logs it and so sends it to Sentry when a DSN
// is set. Fire-and-forget: a report that cannot be sent must never be what breaks the recovery screen. Only structure goes: the error's name and
// message, its stack and React's component stack, which view type was showing and which app - never a record. The server scrubs the message again
// (a JS message is often built from the data), so this is not the only guard. Each distinct crash is sent once per page load, at most 5 in all.
const _reported = new Set();
export function reportClientError(error, info = {}, context = {}) {
  try {
    const key = `${error?.name}|${String(error?.message).slice(0, 120)}|${context.boundary || ''}`;
    if (_reported.has(key) || _reported.size >= 5) return;
    _reported.add(key);
    apiFetch('/api/client_errors', {
      ...OPTS, method: 'POST', headers: JSON_HEADERS, keepalive: true,
      body: JSON.stringify({
        name: error?.name, message: error?.message, stack: error?.stack, component_stack: info?.componentStack,
        boundary: context.boundary, view: context.viewType ? { type: context.viewType } : undefined, app_id: context.appId,
      }),
    }).catch(() => {});
  } catch { /* reporting is best effort */ }
}

// How many records hold `option` of an enum/multiselect field, on the SAVED config: `{ live, trashed }`. A failure answers zeros, since the
// number only decorates a question the person is already being asked.
export async function getOptionUsage(appId, entity, field, option) {
  try {
    const q = new URLSearchParams({ entity, field, option });
    const r = await apiFetch(`/api/apps/${appId}/option_usage?${q}`, OPTS);
    if (!r.ok) return { live: 0, trashed: 0 };
    return await r.json();
  } catch { return { live: 0, trashed: 0 }; }
}

// require_app_admin server-side, which an app's own creator satisfies (they're
// auto-granted ownership when their proposal applies). Two UI callers: the
// "start over" undo on a brand-new app (App.jsx's onAppCreated, where there is
// no earlier version to restore), and the delete flow in the app switcher
// (builder/DeleteAppModal.jsx).
//
// keepData=true retires the app but leaves its tables and rows in the org's
// schema - see backend db.delete_app's docstring. Resolves
// { tables_kept, tables_dropped } either way, so a caller can name what
// happened instead of guessing from the flag it passed.
export async function deleteApp(id, { keepData = false } = {}) {
  const r = await apiFetch(`/api/apps/${id}${keepData ? '?keep_data=1' : ''}`, { ...OPTS, method: 'DELETE' });
  return jsonOrThrow(r, 'Could not delete that app');
}

// --- config version history ---------------------------------------------------
export async function listVersions(appId, limit = 50) {
  const r = await apiFetch(`/api/apps/${appId}/versions?limit=${limit}`, OPTS);
  if (!r.ok) return { versions: [] };
  return r.json().catch(() => ({ versions: [] })); // { versions: [{id, created_by, restored_from, created_at}] }
}
export async function getVersion(appId, versionId) {
  const r = await apiFetch(`/api/apps/${appId}/versions/${versionId}`, OPTS);
  return jsonOrThrow(r, 'Could not load version'); // { id, config, created_by, restored_from, created_at }
}
// Same data-impact gate as saveApp above, and this path needs it more:
// re-saving an old snapshot is the likeliest way to silently drop a field
// added since, whose column then keeps its rows invisible to every view.
export async function restoreVersion(appId, versionId, { acknowledgeDataImpact = false } = {}) {
  const r = await apiFetch(`/api/apps/${appId}/versions/${versionId}/restore`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS,
    body: JSON.stringify(acknowledgeDataImpact ? { acknowledge_data_impact: true } : {}),
  });
  return jsonOrThrow(r, 'Could not restore that version'); // { status, id, restored_from }
}

// Stage a config change for review instead of saving it straight in
// (backend/appdata/proposals.py's api_create_proposal). Needs exactly the
// authority a direct save needs — this is a safer option for someone who can
// already change the config, not a way in for someone who can't.
export async function createProposal(appId, config, comment) {
  const r = await apiFetch(`/api/apps/${appId}/proposals`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS,
    body: JSON.stringify({ config, comment }),
  });
  return jsonOrThrow(r, "Couldn't stage this change for review");
}

// The canAdminApp safe-edit tier (audit §0): a whitelisted inline edit from
// AppOverview, staged as an ordinary config proposal - never a direct write.
export async function submitSafeEditProposal(appId, edits, comment) {
  const r = await apiFetch(`/api/apps/${appId}/safe-edit-proposal`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ edits, comment }),
  });
  return jsonOrThrow(r, 'Could not submit that change'); // the created proposal
}

// --- guarded schema drift fixes (superuser) -------------------------------------
// See backend/admin/drift_fix.py + db/schema.py's plan_drift_fix/start_drift_fix.
// Three-step by design: preview() runs the dry-run cast check and returns a
// plan without touching anything; apply() re-checks live state and, if
// clean, answers 202 with a run row - the DDL executes on a backend thread
// (an ALTER TYPE rewrites the whole table); getSchemaFixRun() is the poll
// target until the run reaches 'applied' | 'blocked' | 'failed' | 'lost'.
export async function listSchemaFixes(appId) {
  const r = await apiFetch(`/api/apps/${appId}/schema_fixes`, OPTS);
  if (!r.ok) return [];
  return r.json().catch(() => []); // [{ kind, entity, field|column, message, ... }]
}
export async function previewSchemaFix(appId, entity, column) {
  const r = await apiFetch(`/api/apps/${appId}/schema_fixes/preview`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ entity, column }) });
  return jsonOrThrow(r, 'Could not preview fix'); // { kind, fixable, sql, risk, bad_count?, sample_failures? }
}
export async function applySchemaFix(appId, entity, column) {
  const r = await apiFetch(`/api/apps/${appId}/schema_fixes/apply`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ entity, column }) });
  const d = await jsonOrThrow(r, 'Could not start fix'); // 409 -> err.body = { error, plan } (refused, no run)
  return d.run; // { id, status: 'running', ... }
}
export async function getSchemaFixRun(appId, runId) {
  const r = await apiFetch(`/api/apps/${appId}/schema_fixes/runs/${runId}`, OPTS);
  return jsonOrThrow(r, 'Could not load fix status'); // { id, status, error, detail, ... }
}
export async function listSchemaFixRuns(appId) {
  const r = await apiFetch(`/api/apps/${appId}/schema_fixes/runs`, OPTS);
  if (!r.ok) return [];
  return r.json().catch(() => []); // [{ id, entity_key, column_name, kind, status, actor_name, started_at, finished_at, error, ... }] newest-first
}

// --- "waiting on you" -----------------------------------------------------------
// One count across all five review queues (backend appdata/waiting.py,
// docs/scheduler-ux-and-ai-suite-seam-scoping-2026-09.md A5). Replaces the
// header's old per-queue polling: the proposals badge used to fetch thirty
// full proposal rows to render one number, and the data-proposal and
// change-request counts were scoped to the app that happened to be open, so
// somebody with three apps saw nothing about the other two.
export async function fetchWaitingOnYou() {
  const r = await apiFetch('/api/waiting-on-you', OPTS);
  return jsonOrThrow(r, 'Could not load your review queues'); // { total, buckets: [{ key, count, oldest_at, app_ids? }] }
}

// --- config change proposals (org admin / superuser) ----------------------------
// Agent-proposed config changes staged behind the approve/reject gate.
export async function listAllProposals(status = '', limit = 30) {
  // Cross-app inbox: doesn't require an app to already be open, so a
  // brand-new agent-proposed app (no `apps` row yet, so absent from the
  // app switcher) is still reachable.
  const qs = new URLSearchParams({ ...(status ? { status } : {}), limit }).toString();
  const r = await apiFetch(`/api/proposals?${qs}`, OPTS);
  return jsonOrThrow(r, 'Could not load proposals'); // { proposals }
}
export async function listProposals(appId, status = '', limit = 20) {
  const qs = new URLSearchParams({ ...(status ? { status } : {}), limit }).toString();
  const r = await apiFetch(`/api/apps/${appId}/proposals?${qs}`, OPTS);
  return jsonOrThrow(r, 'Could not load proposals'); // { proposals, policy, approvals_needed }
}
export async function getProposal(appId, proposalId) {
  const r = await apiFetch(`/api/apps/${appId}/proposals/${proposalId}`, OPTS);
  return jsonOrThrow(r, 'Could not load proposal'); // full row + votes + preview + proposed_config
}
export async function actOnProposal(appId, proposalId, action, comment = '') {
  // action: 'approve' | 'reject'. Approve = one vote; applies at quorum.
  const r = await apiFetch(`/api/apps/${appId}/proposals/${proposalId}/${action}`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ comment }),
  });
  return jsonOrThrow(r, 'Could not update proposal');
}
export async function refreshProposalPreview(appId, proposalId) {
  const r = await apiFetch(`/api/apps/${appId}/proposals/${proposalId}/preview`, { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not refresh preview'); // { preview }
}
export async function editProposal(appId, proposalId, config, comment = '') {
  // Edits a PENDING proposal's document before approving it - supersedes
  // the old row (rejected) with a brand-new pending one (the editor's own
  // identity, zero votes) rather than mutating in place. Returns the NEW
  // proposal. A 400 with body.error === 'validation_failed' carries
  // body.detail, the same [{message}, ...] shape a bad config save
  // anywhere else in the app returns.
  const r = await apiFetch(`/api/apps/${appId}/proposals/${proposalId}/edit`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ config, comment }),
  });
  return jsonOrThrow(r, 'Could not save the edited proposal');
}

// --- file storage (GCS) -------------------------------------------------------
export async function getUploadUrl(appId, entity, field, filename, contentType, size, op = 'create') {
  const r = await apiFetch(`/api/apps/${appId}/files/upload-url`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS,
    body: JSON.stringify({ entity, field, filename, content_type: contentType, size, op }),
  });
  return jsonOrThrow(r, 'Could not get upload URL'); // { upload_url, object_key, content_type }
}
export async function getDownloadUrl(appId, entity, field, key) {
  const qs = new URLSearchParams({ entity, field, key }).toString();
  const r = await apiFetch(`/api/apps/${appId}/files/download-url?${qs}`, OPTS);
  return jsonOrThrow(r, 'Could not get download URL'); // { download_url }
}
// --- signature field (backend/appdata/signatures.py) -------------------------
// What the signer is shown: the statement, the bound values and lines AS THE SERVER RENDERS THEM, and a `view_hash` of exactly that. The same
// call tells the detail drawer whether a signed value is still `valid` or `changed`. A 403 means the viewer may not sign this field.
const signUrl = (appId, entity, id, field) => `/api/apps/${appId}/records/${entity}/${id}/sign/${field}`;
export async function prepareSignature(appId, entity, id, field) {
  const r = await apiFetch(signUrl(appId, entity, id, field), OPTS);
  return jsonOrThrow(r, "That couldn't be loaded — mind trying again?");
}
// `image` is a base64 PNG (a data: prefix is tolerated). A 409 carries `{stale: true}` (the record changed under the signer) or
// `{already: true}`; the thrown Error's `.body` has it.
export async function signRecord(appId, entity, id, field, { image, signer_name, method, view_hash, signer_role }) {
  const r = await apiFetch(signUrl(appId, entity, id, field), {
    ...OPTS, method: 'POST', headers: JSON_HEADERS,
    body: JSON.stringify({ image, signer_name, method, view_hash, ...(signer_role ? { signer_role } : {}) }),
  });
  return jsonOrThrow(r, "That didn't sign — mind trying again?"); // { signed, row }
}
export async function voidSignature(appId, entity, id, field, reason) {
  const r = await apiFetch(`${signUrl(appId, entity, id, field)}/void`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ reason }),
  });
  return jsonOrThrow(r, "That didn't void — mind trying again?"); // { signed: null, row }
}
// The stored picture. Served by this service behind the session cookie, never a signed URL, so an <img src> of it works as it is.
export const signatureImageUrl = (appId, entity, id, field) => `${signUrl(appId, entity, id, field)}/image`;

// Many keys of ONE (entity, field), one call - the `gallery` view's card grid
// (live/GalleryView.jsx) needs every visible row's image at once, which through
// getDownloadUrl above is one request per card. The backend caps a single
// request at MAX_BATCH_DOWNLOAD_KEYS (100), so the caller chunks; each chunk
// still does the full permission resolution, it just does it once instead of
// once per key.
// A deck, with each slide's dataset already resolved for THIS actor
// (docs/deck-engine-scoping-2026-09.md §2.4). One request per deck rather
// than one per slide: `appdata/routes_decks.py` resolves every bind up front
// and de-duplicates a dataset two slides share, so a chart slide and its
// commentary slide cost one query between them.
//
// A deck's address is `(appId, key)`. `appId` is the app of a PACKAGED deck
// (the `decks` config block); it is `null` for a deck authored from the
// Decks tab, which belongs to the org and names an app per slide
// (migration 0102).
function deckUrl(appId, deckKey) {
  const key = encodeURIComponent(deckKey);
  return appId ? `/api/apps/${appId}/decks/${key}` : `/api/decks/${key}`;
}
export async function getDeck(appId, deckKey) {
  const r = await apiFetch(deckUrl(appId, deckKey));
  return jsonOrThrow(r, 'Could not load that deck');
}
// One slide AS EDITED — usually unsaved — resolved for the person editing
// it (`POST /api/decks/preview`), so the editor can show real figures
// rather than `{tokens}`. Reads only: the server runs the slide's datasets
// as this person, exactly as opening the saved deck would. `appId` is the
// app a PACKAGED deck's slides read from; an org deck's slides name theirs.
export async function previewDeckSlide(slide, appId = null) {
  const r = await apiFetch('/api/decks/preview', {
    ...OPTS, method: 'POST', headers: JSON_HEADERS,
    body: JSON.stringify({ slide, ...(appId ? { app: appId } : {}) }),
  });
  const d = await jsonOrThrow(r, 'The figures didn’t load');
  return d.slide || null;
}
// Every deck this person may open: the org decks they wrote or were shared,
// and the packaged decks of every app they can reach.
export async function listAllDecks() {
  const r = await apiFetch('/api/decks');
  const d = await jsonOrThrow(r, 'Could not list your decks');
  return d.decks || [];
}

// Stage an `action` block's action for review (§2.2, Sprint 9). The client
// names a SLIDE and a BLOCK and nothing else: the entity, the action and
// the row are all derived server-side from the deck and its bound dataset,
// which is what keeps this from being a general "run any action" endpoint.
// It never writes — it files a data-write proposal.
export async function stageDeckAction(appId, deckKey, slideIdx, blockIdx) {
  const r = await apiFetch(
    `${deckUrl(appId, deckKey)}/slides/${slideIdx}/blocks/${blockIdx}/stage`,
    { method: 'POST' });
  return jsonOrThrow(r, 'That couldn’t be staged');
}

// A deck as a file (Sprint 10). Bytes, never a link: the response is the
// file itself, so a signed URL never ends up inside a document that
// outlives it. Returns a Blob for the caller to save.
// `notes` asks for the speaker notes in the PPTX's notes pane; the route
// allows it only for somebody who may edit the deck.
export async function exportDeck(appId, deckKey, format, { notes = false } = {}) {
  const r = await apiFetch(
    `${deckUrl(appId, deckKey)}/export`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(notes ? { format, notes: true } : { format }) });
  if (!r.ok) {
    let message = 'That couldn’t be exported';
    try { message = (await r.json()).error || message; } catch { /* not json */ }
    throw new Error(message);
  }
  const disposition = r.headers.get('Content-Disposition') || '';
  const match = /filename="([^"]+)"/.exec(disposition);
  return { blob: await r.blob(), filename: match ? match[1] : `deck.${format}` };
}

export async function listDecks(appId) {
  const r = await apiFetch(`/api/apps/${appId}/decks`);
  const d = await jsonOrThrow(r, 'Could not list decks');
  return d.decks || [];
}

// --- authoring a deck (Sprint 4/4b, migration 0102) -----------------------------
// Anyone in the org may write a deck — it confers nothing, since each slide
// resolves at render time against the VIEWER's own grants in its own app.
// Saving does require that the author can open every app the deck names.
//
// `deckSaveError` exists because the 422 these two return carries a `detail`
// ARRAY from `engine.deck_errors` — one message per problem, each naming the
// slide and block it is about. Collapsing that to `jsonOrThrow`'s single
// sentence would throw away the only part an author can act on, which is the
// mistake docs/fleet-vertical-scoping-2026-09.md §3.8 records for the
// import gate: a backend that explains itself and a client that discards the
// explanation is worse than no explanation.
// `conflict` is set on a 409 from a save made against a stale revision:
// `{revision, by: {name, email, at}, mine}` — who saved since, so the editor
// can ask rather than overwrite them.
export class DeckSaveError extends Error {
  constructor(message, detail, conflict = null) {
    super(message); this.name = 'DeckSaveError'; this.detail = detail || []; this.conflict = conflict;
  }
}

async function deckWrite(url, method, body) {
  const r = await apiFetch(url, { ...OPTS, method, headers: JSON_HEADERS, body: JSON.stringify(body) });
  if (r.ok) return r.json();
  const d = await r.json().catch(() => ({}));
  throw new DeckSaveError(d.error || 'Could not save that deck', d.detail,
                          r.status === 409 ? d.conflict || null : null);
}

// `shares` is `[{user_id, can_edit}]` — who may open it. Omit it on an
// update to leave the list alone; only the author may send it.
export function createDeck(spec, shares = []) {
  return deckWrite('/api/decks', 'POST', { spec, shares });
}
// `baseRevision` is the revision the editor opened at (`getDeck`'s
// `revision`); the server refuses the save with a 409 if anyone has saved
// since. Omit it to overwrite on purpose — restoring a version does.
export function updateDeck(deckId, spec, shares, baseRevision) {
  const body = shares === undefined ? { spec } : { spec, shares };
  if (Number.isInteger(baseRevision)) body.base_revision = baseRevision;
  return deckWrite(deckUrl(null, deckId), 'PUT', body);
}
// A picture for one deck's `image` block. The body IS the file — the route
// sniffs what it actually is, so the declared type is only a courtesy.
export async function uploadDeckAsset(deckId, file) {
  const r = await apiFetch(`${deckUrl(null, deckId)}/assets`, {
    ...OPTS, method: 'POST',
    headers: { 'Content-Type': file.type || 'application/octet-stream',
               'X-Filename': encodeURIComponent(file.name || '') },
    body: file,
  });
  return jsonOrThrow(r, 'That picture didn’t upload');
}
// A `.pptx` as a new private deck. Returns `{id, spec, report}`, the report
// naming everything that did not come across as-is.
export async function importDeck(file) {
  const r = await apiFetch('/api/decks/import', {
    ...OPTS, method: 'POST',
    headers: { 'Content-Type': file.type || 'application/octet-stream',
               'X-Filename': encodeURIComponent(file.name || '') },
    body: file,
  });
  return jsonOrThrow(r, 'That presentation didn’t import');
}
// AI help on one slide (`appdata/deck_assist.py`): `{action: rewrite|notes|
// draft, slide, instruction?, deck_label?}`. A SUGGESTION — nothing is saved.
export async function deckAssist(body) {
  const r = await apiFetch('/api/decks/assist', {
    ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body),
  });
  return jsonOrThrow(r, 'The assistant didn’t answer');
}
// The org's named deck themes (migration 0105). Any member reads them and
// may make one; only its maker or an org admin changes or deletes one, and
// only an org admin picks the DEFAULT. The list answers `{themes,
// can_set_default, font_suggestions}`; one theme is `{id, name, theme,
// brand, is_default, logo, warnings, can_edit, decks}`, where `brand` is
// exactly what a slide draws (the shape the deck routes send as `brand`).
export async function listDeckThemes() {
  return jsonOrThrow(await apiFetch('/api/deck-themes'), 'The deck themes didn’t load');
}
export async function createDeckTheme({ name, theme, makeDefault = false }) {
  const d = await deckWrite('/api/deck-themes', 'POST',
    { name, theme, ...(makeDefault ? { make_default: true } : {}) });
  return d.theme;
}
export async function updateDeckTheme(id, { name, theme } = {}) {
  const body = {};
  if (name !== undefined) body.name = name;
  if (theme !== undefined) body.theme = theme;
  const d = await deckWrite(`/api/deck-themes/${encodeURIComponent(id)}`, 'PUT', body);
  return d.theme;
}
export async function deleteDeckTheme(id) {
  const r = await apiFetch(`/api/deck-themes/${encodeURIComponent(id)}`, { ...OPTS, method: 'DELETE' });
  return jsonOrThrow(r, 'That theme didn’t delete');
}
// `null` leaves the org with no default: decks that never chose go back to
// their app's colours.
export async function setDefaultDeckTheme(id) {
  return deckWrite('/api/deck-themes/default', 'PUT', { theme_id: id || null });
}
// The body IS the picture; the server sniffs what it actually is.
export async function uploadDeckThemeLogo(id, file) {
  const r = await apiFetch(`/api/deck-themes/${encodeURIComponent(id)}/logo`, {
    ...OPTS, method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file,
  });
  return (await jsonOrThrow(r, 'That logo didn’t upload')).theme;
}
// Adopt a picture already on a deck — the logo a PowerPoint import found.
export async function adoptDeckThemeLogo(id, deckId, assetId) {
  const r = await apiFetch(`/api/deck-themes/${encodeURIComponent(id)}/logo`, {
    ...OPTS, method: 'PUT', headers: JSON_HEADERS,
    body: JSON.stringify({ deck_id: deckId, asset_id: assetId }),
  });
  return (await jsonOrThrow(r, 'That logo didn’t save')).theme;
}
export async function clearDeckThemeLogo(id) {
  const r = await apiFetch(`/api/deck-themes/${encodeURIComponent(id)}/logo`, { ...OPTS, method: 'DELETE' });
  return (await jsonOrThrow(r, 'The logo didn’t remove')).theme;
}
export function deckAssetUrl(deckId, assetId) {
  return `${deckUrl(null, deckId)}/assets/${encodeURIComponent(assetId)}`;
}
export async function setDeckShares(deckId, shares) {
  const r = await apiFetch(`${deckUrl(null, deckId)}/shares`,
                           { ...OPTS, method: 'PUT', headers: JSON_HEADERS,
                             body: JSON.stringify({ shares }) });
  return jsonOrThrow(r, 'Who can see it didn’t save');
}
export async function deleteDeck(deckId) {
  const r = await apiFetch(deckUrl(null, deckId), { ...OPTS, method: 'DELETE' });
  return jsonOrThrow(r, 'Could not delete that deck');
}
// The people a deck can be shared with: everyone active in the org.
export async function listDeckPeople() {
  const r = await apiFetch('/api/decks/people');
  const d = await jsonOrThrow(r, 'Could not load the people in your organisation');
  return d.people || [];
}
// Org admins: every deck in the org, by title and author only, to hand on
// or remove. Never its content.
export async function listOrgDecks() {
  const r = await apiFetch('/api/org/decks');
  const d = await jsonOrThrow(r, 'Could not list the decks in your organisation');
  return d.decks || [];
}
export async function reassignDeck(deckId, userId) {
  const r = await apiFetch(`/api/org/decks/${encodeURIComponent(deckId)}/owner`,
                           { ...OPTS, method: 'POST', headers: JSON_HEADERS,
                             body: JSON.stringify({ user_id: userId }) });
  return jsonOrThrow(r, 'That deck couldn’t be handed on');
}

// Who changed what (Sprint 4b). `retention` is the POLICY rather than a
// number — everything from the last N days, never fewer than X, never more
// than Y — so a client must render what it is told rather than hardcoding
// "last 20", which is wrong for exactly the deck being edited hard.
export async function listDeckVersions(deckId) {
  const r = await apiFetch(`${deckUrl(null, deckId)}/versions`);
  return jsonOrThrow(r, 'Could not load this deck’s history');
}
export async function getDeckVersion(deckId, versionId) {
  const r = await apiFetch(
    `${deckUrl(null, deckId)}/versions/${encodeURIComponent(versionId)}`);
  return jsonOrThrow(r, 'Could not load that version');
}

export const MAX_DOWNLOAD_URL_BATCH = 100;
export async function getDownloadUrls(appId, entity, field, keys) {
  const out = {};
  for (let i = 0; i < keys.length; i += MAX_DOWNLOAD_URL_BATCH) {
    const chunk = keys.slice(i, i + MAX_DOWNLOAD_URL_BATCH);
    const r = await apiFetch(`/api/apps/${appId}/files/download-urls`, {
      ...OPTS, method: 'POST', headers: JSON_HEADERS,
      body: JSON.stringify({ entity, field, keys: chunk }),
    });
    const d = await jsonOrThrow(r, 'Could not get download URLs'); // { urls: {key: url} }
    Object.assign(out, d.urls || {});
  }
  return out;
}
export async function commitUpload(appId, entity, field, objectKey, op = 'create') {
  // The upload-completion signal storage/CLAUDE.md long flagged as missing
  // (docs/multimodal-ai-scoping-2026-08.md §1) - tells the backend the PUT
  // actually landed, so it can record real GCS-read size/mime (file_objects,
  // migration 0073) and meter storage_bytes for real instead of at mint
  // time, when nothing yet confirmed the upload would ever happen.
  const r = await apiFetch(`/api/apps/${appId}/files/commit`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS,
    body: JSON.stringify({ entity, field, object_key: objectKey, op }),
  });
  return jsonOrThrow(r, 'Could not confirm the upload'); // { object_key, size_bytes, mime }
}
// How this person hears about one app (backend activity/notify_prefs.py):
// `level` 'all' | 'direct', `email` 'instant' | 'digest' | 'off', `push` boolean (this app's browser notifications).
// `email_enabled` is the account-wide switch, which overrides the email choice.
export async function getNotificationPrefs(appId) {
  const r = await apiFetch(`/api/apps/${appId}/notification-prefs`, { ...OPTS });
  return jsonOrThrow(r, "Couldn't load your notification settings.");
}

export async function setNotificationPrefs(appId, prefs) {
  const r = await apiFetch(`/api/apps/${appId}/notification-prefs`, {
    ...OPTS, method: 'PUT', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(prefs),
  });
  return jsonOrThrow(r, "That setting didn't save \u2014 mind trying again?");
}

// Files attached to a RECORD rather than held in a field (backend
// storage/attachment_routes.py). Same three-step upload a file field uses -
// mint a signed PUT, send the bytes straight to GCS, then commit - except the
// commit here is what makes the file exist as far as the record is
// concerned, so unlike uploadFile's bookkeeping commit it is NOT best-effort.
const attachmentsBase = (appId, entity, recordId) =>
  `/api/apps/${appId}/attachments/${encodeURIComponent(entity)}/${encodeURIComponent(recordId)}`;

export async function listAttachments(appId, entity, recordId) {
  const r = await apiFetch(attachmentsBase(appId, entity, recordId), { ...OPTS });
  if (r.status === 404) return { attachments: [], can_attach: false, unavailable: true };
  return jsonOrThrow(r, "Couldn't load this record's files.");
}

export async function uploadAttachment(appId, entity, recordId, file) {
  const base = attachmentsBase(appId, entity, recordId);
  const r = await apiFetch(`${base}/upload-url`, {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename: file.name, content_type: file.type || 'application/octet-stream', size: file.size }),
  });
  const { upload_url, object_key, content_type } = await jsonOrThrow(r, `${file.name} couldn't be attached.`);
  const put = await fetch(upload_url, { method: 'PUT', headers: { 'Content-Type': content_type }, body: file });
  if (!put.ok) throw new Error(`${file.name} didn't finish uploading \u2014 try it again.`);
  const c = await apiFetch(base, {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ object_key, filename: file.name }),
  });
  return jsonOrThrow(c, `${file.name} couldn't be attached.`); // { attachment }
}

export async function getAttachmentUrl(appId, entity, recordId, attachmentId) {
  const r = await apiFetch(`${attachmentsBase(appId, entity, recordId)}/${attachmentId}/url`, { ...OPTS });
  return jsonOrThrow(r, "Couldn't open that file."); // { download_url, filename, mime }
}

export async function deleteAttachment(appId, entity, recordId, attachmentId) {
  const r = await apiFetch(`${attachmentsBase(appId, entity, recordId)}/${attachmentId}`, { ...OPTS, method: 'DELETE' });
  return jsonOrThrow(r, "That file couldn't be removed.");
}

// "Generate with AI" on a file field (backend storage/image_routes.py).
// Status is per app (it resolves the org's providers) and cached for the
// page's lifetime, since it only changes when an operator reconfigures the
// deployment - a form should not re-ask on every open.
const _imageGenStatus = new Map();
export function getImageGenerationStatus(appId) {
  if (!_imageGenStatus.has(appId)) {
    _imageGenStatus.set(appId, apiFetch(`/api/apps/${appId}/files/generate-image/status`, { ...OPTS })
      .then((r) => (r.ok ? r.json() : { available: false }))
      .catch(() => ({ available: false })));
  }
  return _imageGenStatus.get(appId);
}

export async function generateImageForField(appId, entity, field, prompt, op = 'create') {
  const r = await apiFetch(`/api/apps/${appId}/files/generate-image`, {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entity, field, prompt, op }),
  });
  // { object_key, download_url, mime, size_bytes }
  return jsonOrThrow(r, "That image couldn't be made \u2014 mind trying again?");
}

// Suggestions for the blank fields of a record from the file in one of its
// `file` fields (backend appdata/routes_extract.py). A suggestion only: nothing
// is saved, the caller decides what lands in the form.
// { values: {field: value}, skipped: {field: reason}, asked: [names], source }
// `context`: the form's current values for the prompt's `{field}` tokens (live/extractContext.js).
export async function extractFromFile(appId, entity, field, objectKey, { recordId, fields, context } = {}) {
  const body = { app_id: appId, entity, field, object_key: objectKey };
  if (recordId) body.record_id = recordId;
  if (fields) body.fields = fields;
  if (context && Object.keys(context).length) body.context = context;
  const r = await apiFetch('/api/extract', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, "That file couldn't be read \u2014 mind trying again?");
}

// "Read lines from the file" (a record's sublist, or a new record's line table): SUGGESTED rows for the child entity the field's
// `extract.lines` names. Same rule as above: nothing is saved; the person reviews, then the rows are created through the ordinary data API
// or go into the form's line table to be saved with the record. No `recordId` means the record is still being created.
// { lines: [{field: value}], entity, skipped: [{row, fields}], dropped_rows, truncated, asked, source }
export async function extractLinesFromFile(appId, entity, field, objectKey, recordId, context) {
  const body = { app_id: appId, entity, field, object_key: objectKey, lines: true };
  if (recordId) body.record_id = recordId;
  if (context && Object.keys(context).length) body.context = context;
  const r = await apiFetch('/api/extract', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, "That file couldn't be read \u2014 mind trying again?");
}

export async function uploadFile(appId, entity, field, file, op = 'create') {
  // 1. ask the backend for a signed PUT URL + the object key to store - the
  // real file size is declared up front and signed into the URL (gcs.py's
  // signed_upload_url), so GCS itself enforces it: the browser sets the PUT's
  // Content-Length from the actual body, and any mismatch with what was
  // signed for is a rejected signature, not just a client-side courtesy check.
  const { upload_url, object_key, content_type } = await getUploadUrl(
    appId, entity, field, file.name, file.type || 'application/octet-stream', file.size, op,
  );
  // 2. PUT the bytes straight to GCS - they never pass through our backend
  const put = await fetch(upload_url, { method: 'PUT', headers: { 'Content-Type': content_type }, body: file });
  if (!put.ok) throw new Error('Could not upload that file');
  // 3. tell the backend the upload landed, so it can meter/record it for
  // real - best-effort: the object is already usably in GCS and the
  // caller's record write must not fail over a bookkeeping call, so a
  // commit failure is swallowed here rather than thrown. A never-committed
  // object simply never gets metered or tracked in file_objects, same as
  // every upload before this endpoint existed - not worse than today.
  try {
    await commitUpload(appId, entity, field, object_key, op);
  } catch (e) {
    console.warn('File uploaded but commit failed - it will not be metered or tracked:', e);
  }
  // 4. the caller writes object_key into the record's field value
  return { object_key, filename: file.name, content_type };
}
export async function getImportable() {
  const r = await apiFetch('/api/importable', OPTS);
  if (!r.ok) return [];
  return r.json().catch(() => []); // [{ app_id, app_name, entities:[{key,label}] }]
}

// --- template gallery ---------------------------------------------------------
// `kesher-templates/`'s catalog (docs/recipes-templates-domains-plan-2026-07.md
// Sprint 4, F8a) — TemplateGalleryModal's data source once the gallery stopped
// being five hand-written JS builders. listTemplates() is the cheap header list
// (name/domain/description); getTemplate(name) fetches one template's full
// config skeleton, called lazily only once a template is actually selected.
export async function listTemplates() {
  return jsonOrThrow(await apiFetch('/api/templates', OPTS), 'Could not load templates'); // { templates: [{name, domain, description}] }
}
export async function getTemplate(name) {
  return jsonOrThrow(await apiFetch(`/api/templates/${encodeURIComponent(name)}`, OPTS), 'Could not load template'); // { template: {name, domain, description, config, notes} }
}

// --- feature packs (docs/accounting-packs-scoping-2026-10.md) ---------------------
// Planning only, never a write: each returns the document the chosen packs would give plus a summary of what changes, and the caller
// then saves it through the ordinary saveApp (so validators, the data-impact 409 and approvals all apply). For an installed app,
// `config` is the builder's unsaved draft, so applying a plan never discards edits.
export async function planTemplatePacks(template, enabled) {
  const r = await apiFetch('/api/packs/plan', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ template, enabled }) });
  return jsonOrThrow(r, 'Could not work out what that includes'); // { config, summary, enabled }
}
export async function planAppPacks(appId, enabled, config = undefined) {
  const r = await apiFetch(`/api/apps/${encodeURIComponent(appId)}/packs/plan`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ...(enabled ? { enabled } : {}), ...(config ? { config } : {}) }),
  });
  // `enabled` null = as it is now, which only brings the app's pack list up to date with its template (`adopted` names what is new).
  return jsonOrThrow(r, 'Could not work out that change'); // { config, summary, enabled, changed, adopted, conflicts, data_impact, needs_acknowledgement }
}

// --- marketplace (ROADMAP.md §3.3) --------------------------------------
// listMarketplaceListings() is the published-catalog header list (same
// shape as listTemplates(), plus install_count/version); getMarketplaceListing(id)
// fetches one listing's full config, lazily, only once actually picked -
// same "cheap header, fetch body on demand" split templates use.
export async function listMarketplaceListings(domain) {
  const q = domain ? `?domain=${encodeURIComponent(domain)}` : '';
  return jsonOrThrow(await apiFetch(`/api/marketplace/listings${q}`, OPTS), 'Could not load marketplace listings');
}
export async function getMarketplaceListing(id) {
  return jsonOrThrow(await apiFetch(`/api/marketplace/listings/${encodeURIComponent(id)}`, OPTS), 'Could not load listing');
}
export async function installMarketplaceListing(id, { mode, app_id, name }) {
  const r = await apiFetch(`/api/marketplace/listings/${encodeURIComponent(id)}/install`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ mode, app_id, name }),
  });
  return jsonOrThrow(r, 'Could not install listing'); // { status: 'installed'|'proposed', app_id, proposal?, install }
}
export async function listMyMarketplaceInstalls() {
  return jsonOrThrow(await apiFetch('/api/marketplace/my-installs', OPTS), 'Could not load install history');
}
// --- marketplace admin console (superuser only) -------------------------
export async function adminListMarketplaceListings(status) {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return jsonOrThrow(await apiFetch(`/api/admin/marketplace/listings${q}`, OPTS), 'Could not load listings');
}
export async function adminCreateMarketplaceListing(body) {
  const r = await apiFetch('/api/admin/marketplace/listings', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not create listing');
}
export async function adminUpdateMarketplaceListing(id, body) {
  const r = await apiFetch(`/api/admin/marketplace/listings/${encodeURIComponent(id)}`, { ...OPTS, method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not update listing');
}
export async function adminPublishMarketplaceListing(id) {
  const r = await apiFetch(`/api/admin/marketplace/listings/${encodeURIComponent(id)}/publish`, { ...OPTS, method: 'POST', headers: JSON_HEADERS });
  return jsonOrThrow(r, 'Could not publish listing');
}
export async function adminArchiveMarketplaceListing(id) {
  const r = await apiFetch(`/api/admin/marketplace/listings/${encodeURIComponent(id)}/archive`, { ...OPTS, method: 'POST', headers: JSON_HEADERS });
  return jsonOrThrow(r, 'Could not archive listing');
}
export async function adminDeleteMarketplaceListing(id) {
  const r = await apiFetch(`/api/admin/marketplace/listings/${encodeURIComponent(id)}`, { ...OPTS, method: 'DELETE', headers: JSON_HEADERS });
  return jsonOrThrow(r, 'Could not delete listing');
}
export async function listDemoEnvs() {
  const r = await apiFetch('/api/admin/demo-envs', OPTS);
  if (r.status === 404) return { disabled: true, packs: [], environments: [] };
  return jsonOrThrow(r, 'Could not load demo environments'); // { packs, environments }
}
export async function createDemoEnv(pack, name) {
  const r = await apiFetch('/api/admin/demo-envs', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ pack, name }) });
  return jsonOrThrow(r, 'Could not build the demo'); // { org_id, job_id, email, password }
}
export async function resetDemoEnv(orgId) {
  const r = await apiFetch(`/api/admin/demo-envs/${encodeURIComponent(orgId)}/reset`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: '{}' });
  return jsonOrThrow(r, 'Could not reset the demo');
}
export async function deleteDemoEnv(orgId) {
  const r = await apiFetch(`/api/admin/demo-envs/${encodeURIComponent(orgId)}`, { ...OPTS, method: 'DELETE', headers: JSON_HEADERS });
  return jsonOrThrow(r, 'Could not delete the demo');
}
// Operator console "Evaluations" tab (backend/admin/eval_runs.py): superuser only, every run spends money under a mandatory cap.
export async function listEvals() {
  return jsonOrThrow(await apiFetch('/api/admin/evals', OPTS), 'Could not load the evaluations'); // { harnesses, max_cap_usd, runs, active }
}
export async function listEvalWorkers(orgId) {
  return jsonOrThrow(await apiFetch(`/api/admin/evals/workers?org_id=${encodeURIComponent(orgId)}`, OPTS), 'Could not load that organisation’s workers'); // { org, workers }
}
export async function startEval(body) {
  const r = await apiFetch('/api/admin/evals', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not start the evaluation');
}
export async function getEvalRun(id) {
  return jsonOrThrow(await apiFetch(`/api/admin/evals/${encodeURIComponent(id)}`, OPTS), 'Could not load that run');
}
export async function cancelEval(id) {
  const r = await apiFetch(`/api/admin/evals/${encodeURIComponent(id)}/cancel`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: '{}' });
  return jsonOrThrow(r, 'Could not stop the evaluation');
}
// Operator console "Vendor checks" tab (backend/admin/vendor_checks.py): superuser only. Real calls to a customer's connected vendor accounts with
// that org's own stored credential. `params` is the operator's per-run values ({ account_id: '...' }); the GET sends them as `p.<name>`.
export async function listVendorCheckOrgs(q = '') {
  return jsonOrThrow(await apiFetch(`/api/admin/vendor-checks/orgs?q=${encodeURIComponent(q)}`, OPTS), 'Couldn’t load the list of orgs. Mind trying again?'); // { orgs: [{id, name, vendor_sandbox, is_demo}] }
}
export async function getVendorCheckConnections(orgId, params = {}) {
  const qs = Object.entries(params).map(([k, v]) => `${encodeURIComponent(`p.${k}`)}=${encodeURIComponent(v)}`).join('&');
  const url = `/api/admin/vendor-checks/orgs/${encodeURIComponent(orgId)}/connections${qs ? `?${qs}` : ''}`;
  return jsonOrThrow(await apiFetch(url, OPTS), 'Couldn’t load that org’s connections. Mind trying again?'); // { org, connections, write_confirm }
}
export async function setVendorCheckSandbox(orgId, body) { // { on, confirm?, reason? } -> { vendor_sandbox }
  const r = await apiFetch(`/api/admin/vendor-checks/orgs/${encodeURIComponent(orgId)}/sandbox`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'That flag didn’t change. Mind trying again?');
}
export async function runVendorChecks(body) { // { org_id, connection, mode: 'read'|'write', params?, tool_ids?, confirm?, reason? } -> { results, summary, deferred?, refused? }
  const r = await apiFetch('/api/admin/vendor-checks/run', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'That run didn’t go through. Mind trying again?');
}
// --- receipt and bill capture (backend/appdata/routes_capture.py) -----------------
export async function listCaptureBlocks() {
  return jsonOrThrow(await apiFetch('/api/org/capture-blocks', OPTS), 'Could not load the capture pages'); // { blocks: [{app_id, name, label, max_pages, max_mb, accepts}] }
}
export async function captureUploadUrl(appId, name, { filename, content_type, size }) {
  const r = await apiFetch(`/api/capture/${encodeURIComponent(appId)}/${encodeURIComponent(name)}/upload-url`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ filename, content_type, size }) });
  return jsonOrThrow(r, 'Could not start that upload'); // { upload_url, object_key, content_type }
}
export async function captureCommit(appId, name, objectKey) {
  const r = await apiFetch(`/api/capture/${encodeURIComponent(appId)}/${encodeURIComponent(name)}/commit`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ object_key: objectKey }) });
  return jsonOrThrow(r, 'Could not finish that upload'); // unlike uploadFile(), a failed commit IS an error: the capture needs the file recorded
}
export async function captureCreate(appId, name, objectKeys, source = 'Phone') {
  const r = await apiFetch(`/api/capture/${encodeURIComponent(appId)}/${encodeURIComponent(name)}`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ object_keys: objectKeys, source }) });
  return jsonOrThrow(r, 'Could not hand that over'); // { id, status: 'queued' | 'duplicate' | 'saved' }
}
export async function captureStatus(appId, name, ids) {
  const r = await apiFetch(`/api/capture/${encodeURIComponent(appId)}/${encodeURIComponent(name)}/status?ids=${ids.map(encodeURIComponent).join(',')}`, OPTS);
  return jsonOrThrow(r, 'Could not check on those'); // { captures: { id: { state, figures, warnings, note } } }
}
export async function captureFileUrl(appId, name, key) {
  const r = await apiFetch(`/api/capture/${encodeURIComponent(appId)}/${encodeURIComponent(name)}/file-url?key=${encodeURIComponent(key)}`, OPTS);
  return jsonOrThrow(r, 'Could not open that file'); // { download_url }: the generic file route refuses captured documents on purpose
}
export async function captureReviewQueue() {
  return jsonOrThrow(await apiFetch('/api/org/capture-review', OPTS), 'Could not load the captures to review'); // { items, count }
}
export async function myCaptureAddress() {
  return jsonOrThrow(await apiFetch('/api/me/capture-address', OPTS), 'Could not load your capture address'); // { addresses: [{ app_id, app_name, name, label, address, paused }], aliases: [{ address, verified }], login_address, domain_configured }
}
export async function rotateCaptureAddress(appId, name) {
  const r = await apiFetch(`/api/me/capture-address/${encodeURIComponent(appId)}/${encodeURIComponent(name)}/rotate`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: '{}' });
  return jsonOrThrow(r, 'Could not make a new address'); // { address }
}
export async function addCaptureAlias(address) {
  const r = await apiFetch('/api/me/capture-address/aliases', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ address }) });
  return jsonOrThrow(r, 'Could not add that address'); // { ok, note }: a confirmation link goes to THAT address
}
export async function removeCaptureAlias(address) {
  const r = await apiFetch('/api/me/capture-address/aliases', { ...OPTS, method: 'DELETE', headers: JSON_HEADERS, body: JSON.stringify({ address }) });
  return jsonOrThrow(r, 'Could not remove that address');
}
export async function captureProvenance(appId, entity, id) {
  const r = await apiFetch(`/api/apps/${encodeURIComponent(appId)}/capture-provenance?entity=${encodeURIComponent(entity)}&id=${encodeURIComponent(id)}`, OPTS);
  if (!r.ok) return null;
  return (await r.json().catch(() => ({}))).provenance || null; // { source, read_at, checked_by, checked_at } | null
}
export async function evalFormula(app_id, entity, expr) {
  const r = await apiFetch('/api/eval_formula', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ app_id, entity, expr }) });
  return jsonOrThrow(r, 'Could not evaluate formula'); // { ok, value?, error?, sample_used? }
}
export async function evalRollup(app_id, entity, compute) {
  const r = await apiFetch('/api/eval_rollup', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ app_id, entity, compute }) });
  return jsonOrThrow(r, 'Could not evaluate rollup'); // { ok, value?, error?, sample_used? }
}
export async function evalRoleGrant(app_id, entity, ops, scope) {
  const r = await apiFetch('/api/eval_role_grant', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ app_id, entity, ops, scope }) });
  return jsonOrThrow(r, 'Could not evaluate role grant'); // { ok, total?, visible?, error? }
}
export async function evalValidation(app_id, entity, expr) {
  const r = await apiFetch('/api/eval_validation', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ app_id, entity, expr }) });
  return jsonOrThrow(r, 'Could not evaluate validation rule'); // { ok, total?, failing?, sample_capped?, error? }
}
export async function evalAction(app_id, entity, action) {
  const r = await apiFetch('/api/eval_action', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ app_id, entity, action }) });
  return jsonOrThrow(r, 'Could not evaluate action'); // { ok, entity_label?, row_label?, diffs?, sample_used?, error? }
}
export async function dataOp(app_id, entity, op, extra = {}) {
  const r = await apiFetch('/api/data', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ app_id, entity, op, ...extra }) });
  return jsonOrThrow(r, 'Could not complete that request');
}
// Which other records look like this one (appdata/dupes.py) — the read that
// gives the `merge` op something to point at. Read-only and rate-limited
// server-side. `truncated: true` means the fuzzy pass hit its ceiling, so an
// empty `candidates` means "none among the rows I looked at", NOT "none
// exist" — render the two differently.
export async function duplicateCandidates(app_id, entity, row_id) {
  const r = await apiFetch(`/api/apps/${app_id}/duplicates/${entity}/${row_id}`, OPTS);
  return jsonOrThrow(r, 'Could not look for duplicates');
}
// Fold `loser_id` into `id`: every reference pointing at the loser is
// repointed onto the winner and the loser goes to the trash, in one
// transaction. The loser is SOFT-deleted, so an ordinary restore brings the
// row back for the org's retention window — but the repoint is not undone by
// that, which is what the confirmation copy has to say.
export async function mergeRecords(app_id, entity, id, loser_id) {
  return dataOp(app_id, entity, 'merge', { id, loser_id });
}
// Server-side filtered/paginated rows — the dataset engine's constrained
// entity surface. body: { where, where_any, search, search_fields, order, limit, offset }
// -> { rows, total, limit, offset }
export async function entityQuery(app_id, entity, body = {}) {
  const r = await apiFetch(`/api/apps/${app_id}/query/${entity}`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not load those records');
}
// One update/delete applied to many rows in a single request, each in its
// own transaction (server-side partial success - see routes_data.py's
// api_bulk docstring). body: { op: 'update'|'delete', ids: [...], values }
export async function bulkOp(app_id, entity, body) {
  const r = await apiFetch(`/api/apps/${app_id}/bulk/${entity}`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not apply that change to the selected rows');
}
// Per-user grid layout (column order/hidden/widths) + density (migration
// 0037) - keyed on entity, not view (views have no stable id).
export async function getViewPrefs(app_id, entity) {
  const r = await apiFetch(`/api/apps/${app_id}/view-prefs/${entity}`, OPTS);
  return jsonOrThrow(r, 'Could not load view preferences');
}
export async function saveViewPrefs(app_id, entity, prefs) {
  const r = await apiFetch(`/api/apps/${app_id}/view-prefs/${entity}`, { ...OPTS, method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(prefs) });
  return jsonOrThrow(r, 'Could not save view preferences');
}
// Superuser-only: run an ad-hoc dataset spec (builder's test loop)
export async function previewDataset(app_id, spec, body = {}) {
  const r = await apiFetch(`/api/apps/${app_id}/dataset-preview`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ spec, ...body }) });
  return jsonOrThrow(r, 'Could not preview that dataset');
}
// Run a named dataset from the app config. body: { params, limit, offset }
export async function runDataset(app_id, key, body = {}) {
  const r = await apiFetch(`/api/apps/${app_id}/dataset/${key}`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not load that dataset');
}
export async function runAction(app_id, entity, row_id, action_id) {
  const r = await apiFetch('/api/action', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ app_id, entity, row_id, action_id }) });
  return jsonOrThrow(r, 'Could not run that action'); // { row, message }
}

// --- activity + notifications ------------------------------------------------
export async function fetchActivity(appId, entityKey, recordId) {
  const r = await apiFetch(`/api/apps/${appId}/activity/${entityKey}/${recordId}`, OPTS);
  if (!r.ok) return { events: [] };
  return r.json().catch(() => ({ events: [] }));
}

// --- comments (activity-feed collaboration primitive) ------------------------
// A 404 here means either "no record found" or "comments disabled for this
// entity" (comments.py's _load_ctx doesn't distinguish, deliberately - see
// its own docstring) - callers treat both the same way: no thread to show.
export async function fetchComments(appId, entityKey, recordId) {
  const r = await apiFetch(`/api/apps/${appId}/comments/${entityKey}/${recordId}`, OPTS);
  if (!r.ok) return { comments: [], disabled: r.status === 404 };
  const d = await r.json().catch(() => ({ comments: [] }));
  return { ...d, disabled: false };
}
export async function postComment(appId, entityKey, recordId, body) {
  const r = await apiFetch(`/api/apps/${appId}/comments/${entityKey}/${recordId}`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ body }),
  });
  return jsonOrThrow(r, 'Failed to post comment'); // { comment }
}
export async function editComment(appId, entityKey, recordId, commentId, body) {
  const r = await apiFetch(`/api/apps/${appId}/comments/${entityKey}/${recordId}/${commentId}`, {
    ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ body }),
  });
  return jsonOrThrow(r, 'Failed to edit comment'); // { comment }
}
export async function deleteComment(appId, entityKey, recordId, commentId) {
  const r = await apiFetch(`/api/apps/${appId}/comments/${entityKey}/${recordId}/${commentId}`, {
    ...OPTS, method: 'DELETE',
  });
  return jsonOrThrow(r, 'Failed to delete comment'); // { deleted: true }
}
// [{ id, name }] of this app's members, for the @-mention picker.
export async function fetchMentionable(appId) {
  const r = await apiFetch(`/api/apps/${appId}/comments/mentionable`, OPTS);
  if (!r.ok) return { members: [] };
  return r.json().catch(() => ({ members: [] }));
}

export async function fetchNotifications(appId, unreadOnly = false, { background = false } = {}) {
  const url = `/api/apps/${appId}/notifications${unreadOnly ? '?unread=1' : ''}`;
  const r = await apiFetch(url, { ...OPTS, background });
  if (!r.ok) return { notifications: [], unread: 0 };
  return r.json().catch(() => ({ notifications: [], unread: 0 }));
}
export async function markNotificationsRead(appId, ids = null) {
  const r = await apiFetch(`/api/apps/${appId}/notifications/read`, {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(ids ? { ids } : {}),
  });
  if (!r.ok) return { unread: 0 };
  return r.json().catch(() => ({ unread: 0 }));
}

// Org-wide counterparts (sprint 2026-08 sprint 2) - same shape, not scoped
// to one app_id. Powers org-home's unified feed (OrgNotifications.jsx).
export async function fetchOrgNotifications(unreadOnly = false, { background = false } = {}) {
  const url = `/api/org/notifications${unreadOnly ? '?unread=1' : ''}`;
  const r = await apiFetch(url, { ...OPTS, background });
  if (!r.ok) return { notifications: [], unread: 0 };
  return r.json().catch(() => ({ notifications: [], unread: 0 }));
}
export async function markOrgNotificationsRead(ids = null) {
  const r = await apiFetch('/api/org/notifications/read', {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(ids ? { ids } : {}),
  });
  if (!r.ok) return { unread: 0 };
  return r.json().catch(() => ({ unread: 0 }));
}

// --- workflow ----------------------------------------------------------------
export async function fetchWorkflow(appId, entityKey, recordId) {
  const r = await apiFetch(`/api/apps/${appId}/workflow/${entityKey}/${recordId}`, OPTS);
  if (!r.ok) return null;
  return r.json().catch(() => null);
}
export async function fetchWorkflowInbox(appId) {
  const r = await apiFetch(`/api/apps/${appId}/workflow/inbox`, OPTS);
  if (!r.ok) return { items: [] };
  return r.json().catch(() => ({ items: [] }));
}
export async function workflowAct(appId, instanceId, stepId, action, comment = '') {
  const r = await apiFetch(`/api/apps/${appId}/workflow/act`, {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ instance_id: instanceId, step_id: stepId, action, comment }),
  });
  return (await r.json().catch(() => ({}))); 
}

// --- report ------------------------------------------------------------------
export async function runReport(appId, reportCfg) {
  const r = await apiFetch('/api/report', {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ app_id: appId, report_cfg: reportCfg }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, 'Report error');
  return d; // { columns, rows, totals }
}

// --- ledger ------------------------------------------------------------------
export async function runLedger(appId, ledgerCfg, accountId) {
  const r = await apiFetch('/api/ledger', {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ app_id: appId, ledger_cfg: ledgerCfg, account_id: accountId || null }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, 'Ledger error');
  return d; // { accounts, columns, rows, opening_balance, closing_balance }
}

// --- trial balance -----------------------------------------------------------
export async function runTrialBalance(appId, tbCfg) {
  const r = await apiFetch('/api/trial_balance', {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ app_id: appId, tb_cfg: tbCfg }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, 'Trial balance error');
  return d; // { rows, grand_totals, balanced }
}

// --- recursive rollup --------------------------------------------------------
export async function runRecursiveRollup(appId, rollupCfg) {
  const r = await apiFetch('/api/recursive_rollup', {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ app_id: appId, rollup_cfg: rollupCfg }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, 'Rollup error');
  return d; // { nodes: [...] }
}

// --- intercompany match ------------------------------------------------------
export async function runIntercompanyMatch(appId, iceCfg) {
  const r = await apiFetch('/api/intercompany_match', {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ app_id: appId, ice_cfg: iceCfg }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, 'Intercompany match error');
  return d; // { pairs, summary }
}

// --- balance sheet ------------------------------------------------------------
export async function runBalanceSheet(appId, bsCfg) {
  const r = await apiFetch('/api/balance_sheet', {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ app_id: appId, bs_cfg: bsCfg }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, 'Balance sheet error');
  return d; // { sections, unclassified_roots, unclassified_nodes, subtotals, balanced }
}

// --- income statement --------------------------------------------------------
export async function runIncomeStatement(appId, isCfg) {
  const r = await apiFetch('/api/income_statement', {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ app_id: appId, is_cfg: isCfg }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, 'Income statement error');
  return d; // { sections, unclassified_roots, unclassified_nodes, subtotals, net_income }
}

// --- cash flow statement -----------------------------------------------------
export async function runCashFlowStatement(appId, cfsCfg) {
  const r = await apiFetch('/api/cash_flow_statement', {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ app_id: appId, cfs_cfg: cfsCfg }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, 'Cash flow statement error');
  return d; // { net_income, operating, investing, financing, net_change_in_cash, reconciled }
}

// --- equity statement --------------------------------------------------------
export async function runEquityStatement(appId, esCfg) {
  const r = await apiFetch('/api/equity_statement', {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ app_id: appId, es_cfg: esCfg }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, 'Equity statement error');
  return d; // { rows, totals }
}

// --- global search -----------------------------------------------------------
export async function globalSearch(appId, q) {
  if (!q || q.trim().length < 2) return { results: [] };
  const r = await apiFetch(`/api/search?app_id=${encodeURIComponent(appId)}&q=${encodeURIComponent(q.trim())}`, OPTS);
  if (!r.ok) return { results: [] };
  return r.json().catch(() => ({ results: [] }));
}

// --- org-wide search (sprint 2026-08 sprint 2) --------------------------------
export async function orgSearch(q) {
  if (!q || q.trim().length < 2) return { results: [] };
  const r = await apiFetch(`/api/org/search?q=${encodeURIComponent(q.trim())}`, OPTS);
  if (!r.ok) return { results: [] };
  return r.json().catch(() => ({ results: [] }));
}

// --- saved views ---------------------------------------------------------
export async function listSavedViews(appId, entityKey) {
  const r = await apiFetch(`/api/apps/${appId}/saved-views/${entityKey}`, OPTS);
  if (!r.ok) return { views: [] };
  return r.json().catch(() => ({ views: [] }));
}
// `extra`: { view_type, columns, group_by, chart, pinned } for a personal view that is not a plain table (WP-10.2).
export async function createSavedView(appId, entityKey, name, filters, sort, shared = false, extra = {}) {
  const r = await apiFetch(`/api/apps/${appId}/saved-views/${entityKey}`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS,
    body: JSON.stringify({ name, filters, sort, shared, ...extra }),
  });
  return jsonOrThrow(r, 'Could not save this view');
}
// patch: any of { name, filters, sort, shared } - only the keys present are
// changed. Used for both "update with current filters" and "rename".
export async function updateSavedView(appId, viewId, patch) {
  const r = await apiFetch(`/api/apps/${appId}/saved-views/${viewId}`, {
    ...OPTS, method: 'PATCH', headers: JSON_HEADERS,
    body: JSON.stringify(patch),
  });
  return jsonOrThrow(r, 'Could not update this view');
}
export async function deleteSavedView(appId, viewId) {
  const r = await apiFetch(`/api/apps/${appId}/saved-views/${viewId}`, { ...OPTS, method: 'DELETE' });
  return jsonOrThrow(r, 'Could not delete this view');
}

// --- CSV import --------------------------------------------------------------
// `acknowledge` clears the server's import gate (backend/appdata/data_import.py):
// a file carrying a finding that means the import will NOT do what was asked
// comes back 409 { import_ack_required, import_impact } until the caller says
// go ahead. The error's `body` carries those findings, so ImportModal renders
// them rather than showing the message alone — the same fork DataImpactModal
// already offers for a config save.
// An .xlsx for the import modal, parsed server-side (backend
// appdata/xlsx_import.py) into the same { headers, rows } parseCsv returns,
// so everything after the upload step is identical for either file type.
export async function parseXlsxForImport(appId, entityKey, file, sheet) {
  const q = sheet ? `?sheet=${encodeURIComponent(sheet)}` : '';
  const r = await apiFetch(`/api/apps/${appId}/import/${entityKey}/parse_xlsx${q}`, {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/octet-stream' },
    body: file,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, "That file couldn't be read.");
  return d; // { headers, rows, sheet, sheets, truncated }
}

export async function importRows(appId, entityKey, rows, mapping, upsertKey,
                                 { acknowledge = false } = {}) {
  const r = await apiFetch(`/api/apps/${appId}/import/${entityKey}`, {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      rows, mapping, upsert_key: upsertKey || undefined,
      acknowledge_import: acknowledge || undefined,
    }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, 'Could not import that file');
  return d; // { imported, updated, errors, import_impact?, deferred?, continuation_job_id? }
}
// A dry run of the same file: what it WOULD do, with nothing written.
export async function previewImport(appId, entityKey, rows, mapping, upsertKey) {
  const r = await apiFetch(`/api/apps/${appId}/import/${entityKey}/preview`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS,
    body: JSON.stringify({ rows, mapping, upsert_key: upsertKey || undefined }),
  });
  return jsonOrThrow(r, 'Could not check this file');
}

// --- webhooks ----------------------------------------------------------------
export async function listWebhooks(appId) {
  const r = await apiFetch(`/api/apps/${appId}/webhooks`, OPTS);
  if (!r.ok) return { webhooks: [] };
  return r.json().catch(() => ({ webhooks: [] }));
}
// Recent delivery outcomes for one endpoint (backend/webhooks/CLAUDE.md's
// delivery log). Soft-fails to an empty list like listWebhooks above: this is
// diagnostic detail inside an expander, and it must never be the reason the
// webhook list itself fails to render.
export async function listWebhookDeliveries(appId, webhookId) {
  const r = await apiFetch(`/api/apps/${appId}/webhooks/${webhookId}/deliveries`, OPTS);
  if (!r.ok) return { deliveries: [] };
  return r.json().catch(() => ({ deliveries: [] }));
}
export async function createWebhook(appId, body) {
  const r = await apiFetch(`/api/apps/${appId}/webhooks`, {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, 'Failed');
  return d;
}
export async function deleteWebhook(appId, id) {
  await apiFetch(`/api/apps/${appId}/webhooks/${id}`, { ...OPTS, method: 'DELETE' });
}

// --- outbound webhooks (docs/platform-capability-scoping-2026-08.md §3) -----
export async function listWebhooksOut(appId) {
  const r = await apiFetch(`/api/apps/${appId}/webhooks-out`, OPTS);
  if (!r.ok) return { subscriptions: [] };
  return r.json().catch(() => ({ subscriptions: [] }));
}
export async function listWebhookOutDeliveries(appId, subscriptionId) {
  const r = await apiFetch(`/api/apps/${appId}/webhooks-out/${subscriptionId}/deliveries`, OPTS);
  if (!r.ok) return { deliveries: [] };
  return r.json().catch(() => ({ deliveries: [] }));
}
export async function createWebhookOut(appId, body) {
  const r = await apiFetch(`/api/apps/${appId}/webhooks-out`, {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, 'Failed');
  return d; // includes `secret` once - the caller must show/copy it immediately
}
export async function deleteWebhookOut(appId, id) {
  await apiFetch(`/api/apps/${appId}/webhooks-out/${id}`, { ...OPTS, method: 'DELETE' });
}
export async function reactivateWebhookOut(appId, id) {
  const r = await apiFetch(`/api/apps/${appId}/webhooks-out/${id}/reactivate`, { ...OPTS, method: 'POST' });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw apiErrorFrom(r, d, 'Failed');
  return d;
}

// --- audit trail -------------------------------------------------------------
// Is the record audit intact (WP-4.5)? { ok, checked_rows, sealed_through, unsealed_rows, anchors, latest_anchor_at, problem }.
export async function verifyAudit(appId, scope = 'recent') {
  return jsonOrThrow(await apiFetch(`/api/apps/${appId}/audit/verify?scope=${scope}`, OPTS), 'Could not verify the audit trail');
}
export async function fetchAudit(appId, params = {}) {
  const qs = new URLSearchParams(params).toString();
  const r  = await apiFetch(`/api/apps/${appId}/audit${qs ? '?' + qs : ''}`, OPTS);
  if (!r.ok) return { events: [] };
  return r.json().catch(() => ({ events: [] }));
}

// Fetches the export as a blob (rather than navigating there directly) so the
// HttpOnly session cookie rides along via apiFetch/OPTS the same way every
// other /api call does - a bare <a href> would work too, but only because GETs
// happen to skip the CSRF header; fetch+blob keeps this consistent with the
// rest of the module and gives us a real filename via Content-Disposition.
export async function exportAudit(appId, params = {}, format = 'csv') {
  const qs = new URLSearchParams({ ...params, format }).toString();
  const r  = await apiFetch(`/api/apps/${appId}/audit/export?${qs}`, OPTS);
  if (!r.ok) throw apiErrorFrom(r, await r.json().catch(() => ({})), 'Could not export the audit log');
  const blob = await r.blob();
  const match = /filename="([^"]+)"/.exec(r.headers.get('Content-Disposition') || '');
  const filename = match ? match[1] : `audit-${appId}.${format}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// --- batch jobs ---------------------------------------------------------------
// Every scheduled[] entry with when it last ran and when it runs next - the
// unified operator read behind BatchJobsView's Schedules section. `next_run_at`
// is computed server-side on purpose: it has to come from the same croniter +
// timezone resolution the sweep uses to decide due-ness, or the screen and the
// thing that actually fires would disagree.
export async function fetchSchedules(appId) {
  const r = await apiFetch(`/api/apps/${appId}/scheduled`, OPTS);
  return jsonOrThrow(r, 'Could not load this app\u2019s schedules'); // { scheduled: [...] }
}
// Fire one schedule now instead of waiting for its cron. Runs through the same
// dispatcher the cron does, as the same actor, so it answers "what happens at
// 3am" rather than something adjacent.
export async function runSchedule(appId, scheduleId) {
  const r = await apiFetch(`/api/apps/${appId}/scheduled/${scheduleId}/run`, { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not run that schedule');
}
export async function fetchBatchRuns(appId, jobId, limit = 20) {
  const r = await apiFetch(`/api/apps/${appId}/batch/${jobId}/runs?limit=${limit}`, OPTS);
  if (!r.ok) return { runs: [] };
  return r.json().catch(() => ({ runs: [] }));
}
export async function runBatchJob(appId, jobId) {
  const r = await apiFetch(`/api/apps/${appId}/batch/${jobId}/run`, { ...OPTS, method: 'POST', headers: JSON_HEADERS });
  return jsonOrThrow(r, 'Could not run that batch job'); // { job_id, matched, succeeded, failed, status, deferred?, continuation_job_id?, ... }
}
// The rows a run could not write, and why (backend `batch_run_rows`, migration
// 0084). `sample_errors` on the run row caps at 20 for display; this is the
// full set a retry would actually touch, which is why the two are read from
// one endpoint pair rather than the console guessing from the sample.
export async function fetchFailedRows(appId, jobId, runId, limit = 200) {
  const q = new URLSearchParams({ limit: String(limit) });
  if (runId) q.set('run_id', runId);
  const r = await apiFetch(`/api/apps/${appId}/batch/${jobId}/failed_rows?${q}`, OPTS);
  if (!r.ok) return { run_id: null, rows: [] };
  return r.json().catch(() => ({ run_id: null, rows: [] }));
}
export async function retryFailedRows(appId, jobId, runId) {
  const r = await apiFetch(`/api/apps/${appId}/batch/${jobId}/retry_failed`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS,
    body: JSON.stringify(runId ? { run_id: runId } : {}),
  });
  return jsonOrThrow(r, 'Could not retry the failed rows'); // { matched, succeeded, failed, retried, retried_run_id }
}
// One background job's status (backend/automation/jobs.py). Deliberately one
// job at a time: both callers already hold the id of the job they are waiting
// on — a run's own continuation, or an import's remainder — and a per-org job
// list is a platform-operator surface, not an app-owner one.
export async function fetchJobStatus(appId, jobId) {
  const r = await apiFetch(`/api/apps/${appId}/jobs/${jobId}`, OPTS);
  if (!r.ok) return null;
  return r.json().catch(() => null);
}

// Recurrences (backend/automation/recurrences_run.py). Generation history is
// read back through fetchBatchRuns above, under the `recurrence_<name>` job id
// the compiled trigger already uses — not a second history endpoint.
export async function runRecurrence(appId, name) {
  const r = await apiFetch(`/api/apps/${appId}/recurrences/${name}/run`, { ...OPTS, method: 'POST', headers: JSON_HEADERS });
  return jsonOrThrow(r, 'Could not generate occurrences'); // { recurrence, due, created, skipped, failed, deferred, cap_exceeded, ... }
}
export async function fetchRecurrenceOccurrences(appId, name, limit = 100) {
  const r = await apiFetch(`/api/apps/${appId}/recurrences/${name}/occurrences?limit=${limit}`, OPTS);
  if (!r.ok) return { occurrences: [] };
  return r.json().catch(() => ({ occurrences: [] }));
}

// Computations (docs/advanced-computation-scoping-2026-08.md,
// backend/automation/computations_run.py) — advanced computation as config
// (regression / EMSR-b protection levels / linear programming). Generation
// history reads back through fetchBatchRuns above, under the
// `computation_<name>` job id the compiled trigger already uses.
export async function runComputation(appId, name) {
  const r = await apiFetch(`/api/apps/${appId}/computations/${name}/run`, { ...OPTS, method: 'POST', headers: JSON_HEADERS });
  return jsonOrThrow(r, 'Could not run that computation');
}
// Ad-hoc preview of a DRAFT computation (not yet saved) — dry_run_solve
// against the app's own already-saved entities/datasets. body:
// { computation, name, sample_limit }.
export async function previewComputation(appId, body) {
  const r = await apiFetch(`/api/apps/${appId}/computations/preview`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not preview that computation');
}
// The org's advanced-computation entitlement/quota (§6.4's "locked module,
// always shown, with a remaining-runs count").
export async function fetchComputationQuota(appId) {
  const r = await apiFetch(`/api/apps/${appId}/computations/quota`, OPTS);
  return jsonOrThrow(r, 'Could not load quota');
}
// "Use this result" (§10.3) — generates, never applies, a LOOKUP_WHERE-based
// virtual field or validation-rule spec from a computation's own writes map.
export async function computationUseResult(appId, name, body) {
  const r = await apiFetch(`/api/apps/${appId}/computations/${name}/use_result`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not generate a consumer for this result');
}

// Syncs (docs/fulfillment-vertical-scoping-2026-09.md Sprints 1-2,
// backend/automation/syncs_run.py) — scheduled connector INGEST: call a vendor
// operation on a cron, page through the results, land them as rows.
//
// Unlike batch jobs and recurrences, a sync's run history does NOT read back
// through fetchBatchRuns: it has its own `sync_runs` table because the unit of
// work differs (a PAGE of a vendor response that may land many rows or none,
// against a row of an entity the app already has), so the counters differ too.
//
// `fetchSyncs` is the one call this screen genuinely cannot do without. For a
// poll-based feed, "has this integration quietly stopped" is unanswerable from
// the data alone — a sync that died last Tuesday looks exactly like a vendor
// with nothing new to send — so the last run, the watermark and the
// deactivation state have to come from somewhere that knows.
export async function fetchSyncs(appId) {
  const r = await apiFetch(`/api/apps/${appId}/syncs`, OPTS);
  if (!r.ok) return { syncs: [] };
  return r.json().catch(() => ({ syncs: [] }));
}
export async function fetchSyncRuns(appId, name, limit = 25) {
  const r = await apiFetch(`/api/apps/${appId}/syncs/${name}/runs?limit=${limit}`, OPTS);
  if (!r.ok) return { runs: [] };
  return r.json().catch(() => ({ runs: [] }));
}
// The vendor documents one run could not land, each with the payload that
// produced it. There is no `row_id` to show the way a batch job's failures
// have one — a sync fails on something that never became a row — so the
// document itself is the only thing that makes "why did these 14 not land"
// answerable after the fact.
export async function fetchSyncFailures(appId, name, runId, limit = 200) {
  const r = await apiFetch(`/api/apps/${appId}/syncs/${name}/runs/${runId}/failures?limit=${limit}`, OPTS);
  if (!r.ok) return { failures: [] };
  return r.json().catch(() => ({ failures: [] }));
}
export async function runSync(appId, name) {
  const r = await apiFetch(`/api/apps/${appId}/syncs/${name}/run`, { ...OPTS, method: 'POST', headers: JSON_HEADERS });
  return jsonOrThrow(r, 'Could not run that sync'); // { run_id, status, fetched, landed, failed, skipped, pages, cursor, error }
}
// Re-ingest from an explicit point in time, on the same code path as a
// scheduled run. The window is never promoted onto the sync's high-water mark,
// so backfilling last March cannot rewind the live watermark to March.
export async function backfillSync(appId, name, since) {
  const r = await apiFetch(`/api/apps/${appId}/syncs/${name}/backfill`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ since }),
  });
  return jsonOrThrow(r, 'Could not start that backfill');
}
// Clear a self-deactivation after fixing the credential. Nothing clears it on
// a timer, deliberately — a sync switched itself off because retrying was
// burning the org's call budget, and a timer would just resume that.
export async function reactivateSync(appId, name) {
  const r = await apiFetch(`/api/apps/${appId}/syncs/${name}/reactivate`, { ...OPTS, method: 'POST', headers: JSON_HEADERS });
  return jsonOrThrow(r, 'Could not switch this sync back on');
}

// --- action status poll ------------------------------------------------------
export async function pollActionStatus(sessionId) {
  // Always background: this only ever runs on a timer, waiting for a
  // ticket somebody else has to resolve.
  const r = await apiFetch(`/api/action-status/${sessionId}`, { ...OPTS, background: true });
  if (!r.ok) return { status: 'unknown' };
  return r.json().catch(() => ({ status: 'unknown' }));
}

// --- AI Studio (AI Studio Merge Phase 2) ------------------------------------
// Every call here is org-scoped server-side (backend/ai_studio/ai_studio.py resolves
// org_id from the session, never from anything the client sends) - no appId
// concept at all, unlike the rest of this file. `ai_studio_config_access` is
// a per-user grant independent of is_org_admin/is_superuser - see
// setAiStudioAccess below and PATCH /api/users/<id>.
export async function aiRun(task, sessionId, channel, scope, idempotencyKey, attachments) {
  // `channel` (app_builder is the server default) selects the chat surface;
  // the server maps it to a worker - the client never names a worker directly.
  // `scope` (ROADMAP.md §3.1 "chat has no idea which app/resource you're
  // looking at"): optional { currentAppId } or { currentResourceId,
  // currentResourceType } - whichever's relevant to `channel` - so the
  // server can inject a "the user is currently viewing X" context line.
  // Never trusted as authorization, just a hint re-validated server-side
  // against the caller's own org.
  // `idempotencyKey`: one UUID per logical send (ChatPanel.jsx generates it
  // once when the user submits, not once per HTTP attempt), so a double-
  // click or a retried request for the SAME send collides with itself
  // server-side (ai_engine.run.run_task's idempotency_key) instead of
  // dispatching the run twice.
  // `attachments` (docs/multimodal-ai-scoping-2026-08.md §2 Track C):
  // optional [{object_key}] - each already uploaded via uploadFile() before
  // send() is called (ChatPanel.jsx's paperclip/drop-zone). The server
  // resolves/authorizes each one itself; this is just the list of what to attach.
  const body = {
    task,
    ...(sessionId ? { session_id: sessionId } : {}),
    ...(channel ? { channel } : {}),
    ...(scope?.currentAppId ? { current_app_id: scope.currentAppId } : {}),
    ...(scope?.currentResourceId ? { current_resource_id: scope.currentResourceId } : {}),
    ...(scope?.currentResourceType ? { current_resource_type: scope.currentResourceType } : {}),
    ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
    ...(attachments?.length ? { attachments } : {}),
  };
  const r = await apiFetch('/api/ai/run', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  // 202 pending_approval / 422 blocked are not "failures" - the caller reads
  // the ticket_id/reason off the body itself rather than treating them as errors.
  const d = await r.json().catch(() => ({}));
  if (r.status >= 500) { const err = new Error(d.error || 'Could not run the agent'); err.status = r.status; throw err; }
  return { ...d, __status: r.status };
}
export async function aiApprove(ticketId) {
  const r = await apiFetch('/api/ai/approve', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ticket_id: ticketId }) });
  return jsonOrThrow(r, 'Could not approve this ticket');
}
export async function aiReject(ticketId, reason) {
  const r = await apiFetch('/api/ai/reject', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ticket_id: ticketId, reason }) });
  return jsonOrThrow(r, 'Could not reject this ticket');
}
export async function aiListTickets() {
  const r = await apiFetch('/api/ai/tickets', OPTS);
  return jsonOrThrow(r, 'Could not load pending approvals');
}
// One attachment of a pending email send, for the approver deciding it
// (backend ai_studio.api_ai_ticket_attachment). Bytes, never a link: the
// route is keyed by the ticket, and a signed URL on a card would outlive the
// review. `unavailable` means this viewer can't open it (or it doesn't
// exist) - the card says so, and approving is still allowed.
export async function aiTicketAttachment(ticketId, index) {
  const r = await apiFetch(`/api/ai/tickets/${encodeURIComponent(ticketId)}/attachments/${index}`, OPTS);
  if (r.status === 404) return { unavailable: true };
  if (!r.ok) throw new Error('Could not open this attachment');
  return { blob: await r.blob() };
}
export async function aiListTicketHistory() {
  const r = await apiFetch('/api/ai/tickets/history', OPTS);
  return jsonOrThrow(r, 'Could not load approval history');
}
export async function aiListSessions(channel) {
  // Per-channel session rail: the server treats a legacy (channel-less)
  // session as app_builder, so passing 'app_builder' still surfaces old chats.
  const q = channel ? `?channel=${encodeURIComponent(channel)}` : '';
  const r = await apiFetch(`/api/ai/sessions${q}`, OPTS);
  return jsonOrThrow(r, 'Could not load chat sessions');
}
export async function aiGetSession(sessionId) {
  const r = await apiFetch(`/api/ai/sessions/${encodeURIComponent(sessionId)}`, OPTS);
  return jsonOrThrow(r, 'Could not load this session');
}
export async function aiPatchSession(sessionId, patchBody) {
  const r = await apiFetch(`/api/ai/sessions/${encodeURIComponent(sessionId)}`, { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(patchBody) });
  return jsonOrThrow(r, 'Could not update this session');
}
export async function aiListResources() {
  const r = await apiFetch('/api/ai/resources', OPTS);
  return jsonOrThrow(r, 'Could not load AI Suite configuration');
}
// The AI Suite's agent summary card: chat reach, the app buttons and
// schedules that name this agent (apps the caller can open only), and a
// 30-day run count. backend ai_studio.py's api_ai_agent_overview.
export async function aiAgentOverview(workerId) {
  const r = await apiFetch(`/api/ai/agents/${encodeURIComponent(workerId)}/overview`, OPTS);
  return jsonOrThrow(r, 'Could not load how this agent is used');
}
export async function aiSaveResource(collection, id, config) {
  const r = await apiFetch(`/api/ai/resources/${collection}/${encodeURIComponent(id)}`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(config) });
  return jsonOrThrow(r, 'Could not save');
}
export async function aiArchiveResource(collection, id) {
  const r = await apiFetch(`/api/ai/resources/${collection}/${encodeURIComponent(id)}`, { ...OPTS, method: 'DELETE' });
  return jsonOrThrow(r, 'Could not archive');
}
// Config version history for a worker/tool/policy/model (ROADMAP.md §3.1) -
// the AI-resource counterpart to listVersions/getVersion/restoreVersion above.
export async function aiListVersions(collection, id, limit = 50) {
  const r = await apiFetch(`/api/ai/resources/${collection}/${encodeURIComponent(id)}/versions?limit=${limit}`, OPTS);
  if (!r.ok) return { versions: [] };
  return r.json().catch(() => ({ versions: [] }));
}
export async function aiGetVersion(collection, id, versionId) {
  const r = await apiFetch(`/api/ai/resources/${collection}/${encodeURIComponent(id)}/versions/${versionId}`, OPTS);
  return jsonOrThrow(r, 'Could not load version');
}
export async function aiRestoreVersion(collection, id, versionId) {
  const r = await apiFetch(`/api/ai/resources/${collection}/${encodeURIComponent(id)}/versions/${versionId}/restore`, { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not restore that version');
}
// AI-resource proposal gate (ai_proposals.py): the human review side of an
// agent-proposed worker/tool/model change. Org-scoped server-side, gated by
// the same ai_studio_config_access population as the config screen.
export async function aiListProposals(status) {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  const r = await apiFetch(`/api/ai/proposals${q}`, OPTS);
  return jsonOrThrow(r, 'Could not load AI config proposals');
}
export async function aiGetProposal(id) {
  const r = await apiFetch(`/api/ai/proposals/${encodeURIComponent(id)}`, OPTS);
  return jsonOrThrow(r, 'Could not load this proposal');
}
export async function aiApproveProposal(id, comment) {
  const r = await apiFetch(`/api/ai/proposals/${encodeURIComponent(id)}/approve`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ comment: comment || '' }) });
  return jsonOrThrow(r, 'Could not approve this proposal');
}
export async function aiRejectProposal(id, comment) {
  const r = await apiFetch(`/api/ai/proposals/${encodeURIComponent(id)}/reject`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ comment: comment || '' }) });
  return jsonOrThrow(r, 'Could not reject this proposal');
}
export async function aiRefreshProposalPreview(id) {
  const r = await apiFetch(`/api/ai/proposals/${encodeURIComponent(id)}/preview`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: '{}' });
  return jsonOrThrow(r, 'Could not refresh the preview');
}

// Agent Data Writes Plan Phase 2 (appdata/data_proposals.py): the human
// review side of an agent-staged ROW write (a pre-authored action or a set
// of literal-value creates/updates). App-scoped, gated server-side by
// "requester or app admin" rather than the config-proposal quorum machinery.
export async function listDataProposals(appId, status) {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  const r = await apiFetch(`/api/apps/${appId}/data-proposals${q}`, OPTS);
  return jsonOrThrow(r, 'Could not load proposed writes'); // { proposals, is_admin }
}
// automation/agent_actions.py's read side of pending_agent_actions - a
// gated `worker` row action's own ticket, the same "what's outstanding
// against this row/view" question listDataProposals answers for staged
// data writes, just against the worker-action table instead.
export async function listPendingAgentActions(appId, { status, entityKey, rowId } = {}) {
  const q = new URLSearchParams();
  if (status) q.set('status', status);
  if (entityKey) q.set('entity_key', entityKey);
  if (rowId) q.set('row_id', rowId);
  const qs = q.toString();
  const r = await apiFetch(`/api/apps/${appId}/pending-agent-actions${qs ? `?${qs}` : ''}`, OPTS);
  return jsonOrThrow(r, 'Could not load pending AI actions'); // { tickets }
}
export async function getDataProposal(appId, proposalId) {
  const r = await apiFetch(`/api/apps/${appId}/data-proposals/${proposalId}`, OPTS);
  return jsonOrThrow(r, 'Could not load this proposed write');
}
export async function actOnDataProposal(appId, proposalId, action, comment = '', edits = undefined) {
  // action: 'approve' | 'reject'. `edits` ({ values, lines, line_origin }) is an emailed document's corrections, approve only.
  const body = { comment };
  if (edits) body.edits = edits;
  const r = await apiFetch(`/api/apps/${appId}/data-proposals/${proposalId}/${action}`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body),
  });
  return jsonOrThrow(r, `Could not ${action} this proposed write`);
}
// The records a reviewer may pick for a reference field of an emailed document: `{ choices: [{id, label}], more, no_access }`, read as them.
export async function getDataProposalChoices(appId, proposalId, { entity, field, q = '' }) {
  const params = new URLSearchParams({ field, ...(entity ? { entity } : {}), ...(q ? { q } : {}) });
  const r = await apiFetch(`/api/apps/${appId}/data-proposals/${proposalId}/choices?${params}`, OPTS);
  if (!r.ok) return { choices: [], more: false };
  return r.json().catch(() => ({ choices: [], more: false }));
}
export async function refreshDataProposalPreview(appId, proposalId) {
  const r = await apiFetch(`/api/apps/${appId}/data-proposals/${proposalId}/preview`,
    { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: '{}' });
  return jsonOrThrow(r, 'Could not refresh the preview');
}

// --- AI Studio access grant (org admin) --------------------------------------
export async function setAiStudioAccess(userId, allowed) {
  const r = await apiFetch(`/api/users/${userId}`, { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify({ ai_studio_config_access: allowed }) });
  return jsonOrThrow(r, 'Could not update AI Suite access');
}

// --- Google connect (Gmail + Calendar + Tasks, roadmap Phase 5) -------------
// start is a full popup redirect, not a fetch - see backend/ai_studio/ai_studio.py.
// scopeKeys: subset of ['gmail','calendar','tasks'], user-chosen at connect time.
export function googleConnectUrl(scopeKeys = ['gmail']) {
  return `/api/ai/google/start?scopes=${encodeURIComponent(scopeKeys.join(','))}`;
}
// --- workspace chat instructions (backend/ai_studio/ai_studio.py) -----------
// The workspace's own additions to the chat's locked rules; config-access gated.
export async function aiGetChatInstructions() {
  const r = await apiFetch('/api/ai/chat_instructions', OPTS);
  return jsonOrThrow(r, 'Could not load chat instructions'); // { instructions, max_chars, app_builder_customized }
}
export async function aiSaveChatInstructions(instructions) {
  const r = await apiFetch('/api/ai/chat_instructions', {
    ...OPTS, method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ instructions }),
  });
  return jsonOrThrow(r, 'Could not save chat instructions'); // { instructions }
}

// --- per-user tool connection connect (backend/ai_studio/routes_tool_connections.py)
// Also a full popup redirect: the vendor's OAuth screen, then a page that
// postMessages {type: 'tool_connection-connected', ok, message, slug} back.
// --- Chat-generated video (backend storage/video_routes.py) ------------------
// The chat's generate_video tool only PROPOSES a clip; these are the calls a
// person's card makes. `ticket` is the signed request the tool minted.
export async function getVideoRequest(appId, ticket) {
  const r = await apiFetch(`/api/apps/${encodeURIComponent(appId)}/video/request?ticket=${encodeURIComponent(ticket)}`, OPTS);
  return jsonOrThrow(r, 'Could not load this video request');
}
export async function startVideo(appId, ticket) {
  const r = await apiFetch(`/api/apps/${encodeURIComponent(appId)}/video/start`,
    { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ ticket }) });
  return jsonOrThrow(r, 'That didn\u2019t start \u2014 mind trying again?'); // { job_id, status, started }
}
export async function getVideoJob(appId, jobId) {
  const r = await apiFetch(`/api/apps/${encodeURIComponent(appId)}/jobs/${encodeURIComponent(jobId)}`, OPTS);
  return jsonOrThrow(r, 'Could not check on this video'); // { status, result, error, ... }
}
export function generatedVideoUrl(appId, objectKey) {
  return `/api/apps/${encodeURIComponent(appId)}/files/generated-video?key=${encodeURIComponent(objectKey)}`;
}

export function toolConnectionConnectUrl(slug) {
  return `/api/ai/tool_connections/${encodeURIComponent(slug)}/connect/start`;
}
// Per-user connection status, and switching which vendor account (Xero: which
// client) it acts on. Backend ai_studio/routes_tool_connections.py.
export async function toolConnectionStatus(slug) {
  const r = await apiFetch(`/api/ai/tool_connections/${encodeURIComponent(slug)}/connect/status`, OPTS);
  return jsonOrThrow(r, 'Could not check this connection'); // { connected, switchable?, tenant?: {id,label}|null }
}
export async function toolConnectionTenants(slug) {
  const r = await apiFetch(`/api/ai/tool_connections/${encodeURIComponent(slug)}/connect/tenants`, OPTS);
  return jsonOrThrow(r, 'Could not load your organisations'); // { tenants: [{id,label}], current }
}
export async function toolConnectionSwitchTenant(slug, tenantId) {
  const r = await apiFetch(`/api/ai/tool_connections/${encodeURIComponent(slug)}/connect/tenant`,
    { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ tenant_id: tenantId }) });
  return jsonOrThrow(r, 'That didn\u2019t switch \u2014 mind trying again?'); // { tenant: {id,label} }
}
export async function aiGoogleStatus() {
  const r = await apiFetch('/api/ai/google/status', OPTS);
  return jsonOrThrow(r, 'Could not load Google connection status');
}
export async function aiGoogleDisconnect() {
  const r = await apiFetch('/api/ai/google/disconnect', { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not disconnect Google');
}

// --- Bring-your-own-key (org-level LLM provider API keys) --------------------
// GET is open to any org member (knowing what's configured isn't sensitive);
// PUT/DELETE 403 server-side for anyone who isn't an org admin - see
// backend/billing/routes_byok.py. Shaped as one list call + one set/delete
// call per provider (not one endpoint per provider) so a second BYOK-able
// provider is a new row in the same response, not a new function here.
export async function byokList() {
  const r = await apiFetch('/api/org/byok', OPTS);
  return jsonOrThrow(r, 'Could not load your organization\'s API keys'); // { providers: { [provider]: { last_four, validated_at } } }
}
export async function byokSet(provider, apiKey) {
  const r = await apiFetch(`/api/org/byok/${provider}`, { ...OPTS, method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify({ api_key: apiKey }) });
  return jsonOrThrow(r, 'Could not save that API key');
}
export async function byokDelete(provider) {
  const r = await apiFetch(`/api/org/byok/${provider}`, { ...OPTS, method: 'DELETE' });
  return jsonOrThrow(r, 'Could not remove that API key');
}

// --- API keys (ROADMAP.md §3.1 "the technical buyer's front door") ---------
// Self-service /api/v1 API key management - org-admin-gated end to end
// (see backend/apiv1/routes_api_keys.py), unlike byok*'s member-readable/
// admin-writable split above: an API key IS the write surface, so there's
// no lesser "just tell me what's connected" read to open to every member.
export async function apiKeysList() {
  const r = await apiFetch('/api/api-keys', OPTS);
  return jsonOrThrow(r, 'Could not load API keys'); // { keys: [...] }
}
export async function apiKeysCreate({ name, scopes, appId, expiresInDays }) {
  const body = { name, scopes };
  if (appId) body.app_id = appId;
  if (expiresInDays) body.expires_in_days = expiresInDays;
  const r = await apiFetch('/api/api-keys', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not create that API key'); // includes `key` (plaintext) exactly this once
}
export async function apiKeysRevoke(keyId) {
  const r = await apiFetch(`/api/api-keys/${keyId}`, { ...OPTS, method: 'DELETE' });
  return jsonOrThrow(r, 'Could not revoke that API key');
}

// --- Tool connections (docs/external-api-tools-plan.md §1-5) ----------------
// Org-level, secret-bearing third-party API connections that an `external`
// tool references by slug (ConfigEditor.jsx's connection_select widget).
// Unlike BYOK above, every verb here - including the list/read - requires
// ai_studio_config_access, not just org-admin (see
// backend/ai_studio/routes_tool_connections.py): a tool connection is part
// of the AI config surface, not a billing-tier decision, so there is no
// member-readable/admin-writable split to mirror here.
export async function aiListToolConnections() {
  const r = await apiFetch('/api/ai/tool_connections', OPTS);
  return jsonOrThrow(r, 'Could not load tool connections'); // { connections: [{slug,last_four,validated_at,created_at,updated_at}] }
}
export async function aiGetToolConnection(slug) {
  const r = await apiFetch(`/api/ai/tool_connections/${encodeURIComponent(slug)}`, OPTS);
  return jsonOrThrow(r, 'Could not load this connection'); // redacted - never includes credential/client_secret
}
export async function aiSaveToolConnection(slug, blob) {
  const r = await apiFetch(`/api/ai/tool_connections/${encodeURIComponent(slug)}`, { ...OPTS, method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(blob) });
  return jsonOrThrow(r, 'Could not save this connection');
}
export async function aiDeleteToolConnection(slug) {
  const r = await apiFetch(`/api/ai/tool_connections/${encodeURIComponent(slug)}`, { ...OPTS, method: 'DELETE' });
  return jsonOrThrow(r, 'Could not remove this connection');
}
// "Send test call" (Sprint 5): dispatches through the identical rate-limit/
// OAuth/SSRF/retry path a real tool call uses - see
// ai_engine/core/executor.py's _dispatch_external. sampleParams is sent as
// the request body/query per that connection's (or, for aiTestTool, that
// tool's) own method/param_placement; the response is { status_code, body }
// with body already truncated server-side.
export async function aiTestToolConnection(slug, sampleParams) {
  const r = await apiFetch(`/api/ai/tool_connections/${encodeURIComponent(slug)}/test`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(sampleParams || {}) });
  return jsonOrThrow(r, 'Could not test that connection');
}
export async function aiTestTool(toolId, sampleParams) {
  const r = await apiFetch(`/api/ai/tools/${encodeURIComponent(toolId)}/test`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(sampleParams || {}) });
  return jsonOrThrow(r, 'Could not test that tool');
}

// --- Connector templates (docs/connector-templates-plan.md §6-9) ------------
// Three trust tiers, same gating as tool_connections above (installing/
// authoring writes the AI config surface, so every verb here is
// ai_studio_config_access-gated, including list/read).
//
// Platform tier: git-versioned, read-only, installable by every org.
export async function aiListConnectorTemplates() {
  const r = await apiFetch('/api/ai/connector_templates', OPTS);
  return jsonOrThrow(r, 'Could not load connector templates'); // { templates: [{slug,label,description,auth_type,host}] }
}
// What this workspace can actually call, grouped by connection — the source
// for the builder's connector-action picker. Member-readable: it returns no
// credential, only slugs, operation ids and each operation's own JSON Schema.
export async function aiListInstalledOperations() {
  const r = await apiFetch('/api/ai/installed_operations', OPTS);
  return jsonOrThrow(r, 'Could not load what this workspace can connect to'); // { connections: [{connection, operations}] }
}
// The agents this workspace has, id + label only. Member-readable for the
// same reason aiListInstalledOperations above is: a `worker` row action and
// a scheduled[] trigger are both authored by an app admin in the BUILDER,
// who need not hold AI Suite config access. Returns no prompt, no tool list.
export async function aiListAgents() {
  const r = await apiFetch('/api/ai/agents', OPTS);
  return jsonOrThrow(r, 'Could not load this workspace\u2019s agents'); // { agents: [{id,label,description,plan_locked}] }
}
// Open "we need this integration" requests. Config-access-gated: the
// audience is whoever can act on them.
export async function aiListConnectorRequests() {
  const r = await apiFetch('/api/ai/connector_requests', OPTS);
  return jsonOrThrow(r, 'Could not load connector requests'); // { requests: [{slug,label,request_count,last_reason}] }
}
// The member-facing half: ask an admin to connect something. Any member -
// the person who needs an integration is usually not the one who can
// install it, and the gallery is where they're standing when they find out.
export async function aiRequestConnector(slug, reason) {
  const r = await apiFetch('/api/ai/connector_requests', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ slug, reason }) });
  return jsonOrThrow(r, 'Could not pass that on'); // { requested, label, request_count } | { requested:false, already_connected:true }
}
// Connections this org already has that a given bundle can be installed
// against (install_bundle's connection_mode 'existing'), so a second bundle
// from the same vendor doesn't mean re-pasting the same credential. The
// backend matches exactly what install accepts, so anything returned here
// is a safe offer.
export async function aiListReusableConnections(authType, baseUrl, slug) {
  const q = new URLSearchParams({ auth_type: authType || '', base_url: baseUrl || '' });
  if (slug) q.set('slug', slug); // lets the server apply the bundle's own host rule (environments)
  const r = await apiFetch(`/api/ai/reusable_connections?${q}`, OPTS);
  return jsonOrThrow(r, 'Could not check your existing connections'); // { connections: [{slug,last_four,environment?}] }
}
export async function aiGetConnectorTemplate(slug) {
  const r = await apiFetch(`/api/ai/connector_templates/${encodeURIComponent(slug)}`, OPTS);
  return jsonOrThrow(r, 'Could not load this connector template'); // full bundle: {label,description,base_url,auth,operations}
}
export async function aiInstallConnectorTemplate(slug, body) {
  const r = await apiFetch(`/api/ai/connector_templates/${encodeURIComponent(slug)}/install`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body || {}) });
  return jsonOrThrow(r, 'Could not install this connector');
}
// Org-private tier: this workspace's own templates - authored by an org
// admin by hand, or by the connector_template_builder agent tool (see
// InlineConnectorTemplateCard.jsx). Full CRUD, unlike the platform tier.
export async function aiListOrgConnectorTemplates() {
  const r = await apiFetch('/api/ai/org_connector_templates', OPTS);
  return jsonOrThrow(r, 'Could not load your workspace’s connector templates'); // { templates: [{slug,bundle,created_at,updated_at}] }
}
export async function aiGetOrgConnectorTemplate(slug) {
  const r = await apiFetch(`/api/ai/org_connector_templates/${encodeURIComponent(slug)}`, OPTS);
  return jsonOrThrow(r, 'Could not load this connector template');
}
export async function aiDeleteOrgConnectorTemplate(slug) {
  const r = await apiFetch(`/api/ai/org_connector_templates/${encodeURIComponent(slug)}`, { ...OPTS, method: 'DELETE' });
  return jsonOrThrow(r, 'Could not remove this connector template');
}
export async function aiInstallOrgConnectorTemplate(slug, body) {
  const r = await apiFetch(`/api/ai/org_connector_templates/${encodeURIComponent(slug)}/install`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body || {}) });
  return jsonOrThrow(r, 'Could not install this connector');
}
// Verification checks (docs/install-verification-sweep-scoping-2026-09.md
// Sprint 3). `aiConnectorChecks` reads what's stored - the org-credential
// rows plus the caller's OWN per-user rows, never a colleague's.
// `aiVerifyConnection` re-runs the sweep, stores the results, and
// re-attaches anything that now works; it dispatches real outbound calls
// with the org's credential, which is why it's a POST and config-gated.
export async function aiConnectorChecks(slug) {
  const r = await apiFetch(`/api/ai/tool_connections/${encodeURIComponent(slug)}/checks`, OPTS);
  return jsonOrThrow(r, 'Could not load this connection’s checks'); // { slug, checks: [{tool_id,status,status_code,message,checked_at}] }
}
export async function aiVerifyConnection(slug) {
  const r = await apiFetch(`/api/ai/tool_connections/${encodeURIComponent(slug)}/verify`, { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not re-check this connection'); // { verification, recorded, reattached, attach_errors }
}
// Spec import (docs/api-spec-import-scoping-2026-09.md): an OpenAPI document
// or one curl command -> candidate operations -> an org-private template.
// Two calls, deliberately: `parse` writes nothing at all, and the SELECTION
// between them is the feature - importing a large vendor spec wholesale
// would attach hundreds of tools to an agent, each costing context on every
// turn. Neither call installs anything; that stays the human-only install
// flow it already was.
export async function aiParseApiSpec(body) {
  const r = await apiFetch('/api/ai/spec_import/parse', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body || {}) });
  return jsonOrThrow(r, 'Could not read that API description'); // { base_url, auth, auth_candidates, operations, dropped, warnings }
}
export async function aiSaveImportedConnector(body) {
  const r = await apiFetch('/api/ai/spec_import/bundle', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body || {}) });
  return jsonOrThrow(r, 'Could not save this connector template'); // { slug, operations, next_step }
}
// MCP import (docs/mcp-connector-import-scoping-2026-10.md): a vendor's MCP server address -> what it offers -> an org-private template.
// `probe` calls no tool and writes nothing; `bundle` saves a DRAFT and installs nothing. The address carries a secret: it is sent to
// both calls (the server does not keep it between them) and is never stored by either.
export async function aiProbeMcpServer(url) {
  const r = await apiFetch('/api/ai/mcp_import/probe', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ url }) });
  return jsonOrThrow(r, 'Could not read that MCP server'); // { server, tools, dropped, passthrough, warnings, stateless, needs_session, suggestions, credential_kind, base_url }
}
export async function aiSaveMcpConnector(body) {
  const r = await apiFetch('/api/ai/mcp_import/bundle', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body || {}) });
  return jsonOrThrow(r, 'Could not save this connector template'); // { slug, operations, skipped, credential_kind, next_step }
}
// For an OAuth-protected MCP server: once its sign-in connection is installed and connected, read its tools THROUGH that connection
// (listing them needs a token). Same result shape as aiProbeMcpServer, plus `connection_slug` for aiSaveMcpConnector.
export async function aiDiscoverMcpTools(slug) {
  const r = await apiFetch('/api/ai/mcp_import/discover', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ slug }) });
  return jsonOrThrow(r, 'Could not read that server’s tools');
}
// Spec discovery (docs/spec-discovery-scoping-2026-09.md): a vendor NAME,
// no URL required - the connector gallery's own no-results miss calls this
// directly (no agent/LLM turn involved, it's a structured name from a
// search box). On confirm, the frontend feeds the returned `url` straight
// into aiParseApiSpec above - nothing about that pipeline changes.
export async function aiDiscoverApiSpec(vendorName) {
  const r = await apiFetch('/api/ai/spec_import/discover', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ vendor_name: vendorName }) });
  return jsonOrThrow(r, 'Could not search for that API'); // { source, url?, title?, slug?, label?, confidence_tier?, confidence_label? }
}
export async function aiConfirmDiscoveredApiSpec(vendorName) {
  const r = await apiFetch('/api/ai/spec_import/discover/confirm', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ vendor_name: vendorName }) });
  return jsonOrThrow(r, 'Could not record that confirmation');
}
// Shared tier: connector-kind marketplace listings (docs/connector-templates-
// plan.md §6.6) - superuser-published, installable by any org. `getMarketplace
// Listing` (below, the app-template listing detail fetch) already returns a
// connector listing's `config` too, so only list+install are new here; a
// connector install has its own verb (install_connector) since it writes
// tool_connections + tools, never an app, unlike the app-template route.
export async function listMarketplaceConnectorListings() {
  const r = await apiFetch('/api/marketplace/listings?kind=connector', OPTS);
  return jsonOrThrow(r, 'Could not load shared connector templates'); // { listings: [{id,slug,name,description,...}] }
}
export async function installMarketplaceConnectorListing(listingId, body) {
  const r = await apiFetch(`/api/marketplace/listings/${encodeURIComponent(listingId)}/install_connector`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body || {}) });
  return jsonOrThrow(r, 'Could not install this connector');
}

// --- Adoption signal (docs/adoption-and-change-requests-scoping-2026-08.md §1)
// Fire-and-forget: never awaited by the caller, never throws, and a failure
// is deliberately invisible. A lost engagement row is one missing dot on an
// adoption chart, so there is nothing for a user to act on and nothing worth
// retrying — surfacing an error here would only add noise to a view switch
// that otherwise worked perfectly.
export function recordEngagement(app_id, view_key) {
  if (!app_id || !view_key) return;
  apiFetch('/api/engagement', {
    ...OPTS, method: 'POST', headers: JSON_HEADERS,
    body: JSON.stringify({ app_id, view_key }),
  }).catch(() => {});
}

// --- Change requests (docs/adoption-and-change-requests-scoping-2026-08.md §2)
// The door from a rendered live app back into the config-change machine.
// Filing one is inert text — it never becomes a config proposal on its own,
// so this needs no config-change grant, only being able to open the app.
export async function fileChangeRequest({ app_id, body, entity_key, view_key, record_id }) {
  const r = await apiFetch('/api/change-requests', {
    ...OPTS, method: 'POST', headers: JSON_HEADERS,
    body: JSON.stringify({ app_id, body, entity_key, view_key, record_id }),
  });
  return jsonOrThrow(r, "That didn't send — mind trying again?");
}
// An app admin sees every request for the app; anyone else gets their own
// back (they still need to know whether what they asked for was looked at).
// The response's `can_admin` says which of the two you got.
export async function listChangeRequests(app_id, params = {}) {
  const qs = new URLSearchParams(params).toString();
  const r = await apiFetch(`/api/apps/${app_id}/change-requests${qs ? `?${qs}` : ''}`, { ...OPTS });
  return jsonOrThrow(r, 'Could not load change requests');
}
export async function updateChangeRequest(id, patch) {
  const r = await apiFetch(`/api/change-requests/${id}`, {
    ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(patch),
  });
  return jsonOrThrow(r, 'Could not update this request');
}
// Marks the request `planned` and returns the prompt to open the config agent
// with — the route deliberately doesn't run the agent itself, so the hand-off
// stays an ordinary chat turn under that path's existing budget and gates.
export async function handoffChangeRequest(id) {
  const r = await apiFetch(`/api/change-requests/${id}/handoff`, { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not hand this to the agent');
}
// The app-health read (adoption §1.5): active-user counts and their trend, a
// per-view breakdown that includes views nobody has opened, and the members
// who were given access but never came. App admin only.
export async function getAppAdoption(app_id) {
  const r = await apiFetch(`/api/apps/${app_id}/adoption`, { ...OPTS });
  return jsonOrThrow(r, "Couldn't load this app's health just now.");
}
// Nudge one person from the never-opened list: a reminder email linking to
// the app, or - if they never accepted their invite - the invite again.
// Throttled server-side to once a day per person per app.
export async function remindAppMember(app_id, user_id) {
  const r = await apiFetch(`/api/apps/${app_id}/adoption/remind`, {
    ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ user_id }),
  });
  return jsonOrThrow(r, "That reminder didn't go out \u2014 mind trying again?");
}
// Field fill rates per entity — how often each stored field actually carries
// a value ("nobody has filled cost_center in six months"). A separate call
// from getAppAdoption because it scans real entity tables, so a large
// tenant's slow answer never delays the numbers that are cheap.
export async function getAppFieldUsage(app_id) {
  const r = await apiFetch(`/api/apps/${app_id}/field-usage`, { ...OPTS });
  return jsonOrThrow(r, "Couldn't work out which fields get used.");
}

// ---------------------------------------------------------------------------
// Tokenised form links (docs/tokenised-form-links-scoping-2026-09.md)
// ---------------------------------------------------------------------------

// The PUBLIC pair. Deliberately NOT spread with OPTS: those carry
// `credentials: 'same-origin'`, and a form-link request must send no cookie
// at all. A recipient who happens to also be signed in to this deployment
// must not have their session ride along — the token is the entire authority
// and mixing the two would make "what did this request act as" ambiguous.
/* The public pair prefers the body's `message` over its `error`, unlike
 * jsonOrThrow above. Every refusal from routes_form_links.py's public half
 * carries a machine code in `error` (`link_unavailable`) and the sentence
 * meant for a human in `message` — and the human here is a stranger with a
 * link, not a developer with a console, so showing them the code would be
 * showing them nothing. A validation failure has no `message` and its
 * `error` IS the sentence, which is why both are tried. */
async function publicJsonOrThrow(r, fallback) {
  const d = await r.json().catch(() => ({}));
  if (r.ok) return d;
  const err = new Error((d && (d.message || d.error)) || fallback);
  err.status = r.status;
  err.body = d;
  throw err;
}

export async function getPublicForm(token) {
  const r = await fetch(`/api/public/forms/${encodeURIComponent(token)}`,
                        { credentials: 'omit' });
  return publicJsonOrThrow(r, 'This link is no longer active.');
}

/* A form link's attachment (docs/tokenised-form-links-scoping-2026-09.md
 * Phase 2). Deliberately NOT uploadFile() above: that one is three calls
 * (mint, PUT, commit) and this one is two, because the public submit does
 * the commit itself — there is no unauthenticated commit route to call. The
 * caller writes the returned object_key into the field's value, and the
 * submit is what confirms the bytes actually landed. */
export async function uploadPublicFormFile(token, field, file) {
  const r = await fetch(`/api/public/forms/${encodeURIComponent(token)}/upload-url`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      field,
      filename: file.name,
      content_type: file.type || '',
      size: file.size,
    }),
  });
  const { upload_url, object_key, content_type } =
    await publicJsonOrThrow(r, "That file couldn't be attached.");
  const put = await fetch(upload_url, {
    method: 'PUT',
    headers: { 'Content-Type': content_type },
    body: file,
  });
  // GCS rejects a PUT whose real Content-Length differs from the one signed
  // into the URL, so this is where an under-declared size surfaces.
  if (!put.ok) throw new Error("That file didn't upload — mind trying again?");
  return { object_key, filename: file.name };
}

/* Share links — the read-only twin of the form-link pair above. `credentials:
 * 'omit'` for the same reason: the recipient may well also be signed in to
 * this deployment in another tab, and a share link must resolve to exactly
 * what the token grants, never to whatever their session happens to allow. */
export async function getPublicShare(token) {
  const r = await fetch(`/api/public/shares/${encodeURIComponent(token)}`,
                        { credentials: 'omit' });
  return publicJsonOrThrow(r, 'This link is no longer active.');
}
export async function getPublicShareFileUrls(token, keys) {
  const r = await fetch(`/api/public/shares/${encodeURIComponent(token)}/file-urls`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ keys }),
  });
  return publicJsonOrThrow(r, 'Could not load the images.');
}

/* Issuer-facing share-link management. Ordinary authenticated calls. */
export async function listShareLinks(appId, all = false) {
  const r = await apiFetch(`/api/apps/${appId}/share-links${all ? '?all=1' : ''}`, OPTS);
  return jsonOrThrow(r, 'Could not load share links');
}
export async function createShareLink(appId, body) {
  const r = await apiFetch(`/api/apps/${appId}/share-links`, {
    ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body),
  });
  return jsonOrThrow(r, 'Could not create the share link');
}
export async function revokeShareLink(appId, linkId) {
  const r = await apiFetch(`/api/apps/${appId}/share-links/${linkId}`,
                           { ...OPTS, method: 'DELETE' });
  return jsonOrThrow(r, 'Could not revoke the share link');
}

export async function submitPublicForm(token, values, submitted_name) {
  const r = await fetch(`/api/public/forms/${encodeURIComponent(token)}`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ values, submitted_name }),
  });
  return publicJsonOrThrow(r, "That didn't save — mind trying again?");
}

/* Public intake (docs/public-intake-scoping-2026-10.md): an anonymous,
 * create-only form. The key is public and says nothing about the tenant; like
 * the form-link calls above these send no cookie, so a visitor who is also
 * signed in here is never acting as that session. */
export async function getPublicIntake(key) {
  const r = await fetch(`/api/public/intake/${encodeURIComponent(key)}`, { credentials: 'omit' });
  return publicJsonOrThrow(r, 'This form is not available right now.');
}

export async function submitPublicIntake(key, payload) {
  const r = await fetch(`/api/public/intake/${encodeURIComponent(key)}`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return publicJsonOrThrow(r, "That didn't send — mind trying again?");
}

// The survey door (docs/anonymous-responses-scoping-2026-10.md P2b). No cookie, ever:
// a respondent who is also signed in here must not have that session ride along.
export async function getSurveyInvite(token) {
  const r = await fetch(`/api/public/survey/${encodeURIComponent(token)}`, { credentials: 'omit' });
  return publicJsonOrThrow(r, 'This link is not available.');
}

export async function submitSurveyInvite(token, values) {
  const r = await fetch(`/api/public/survey/${encodeURIComponent(token)}`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ values }),
  });
  return publicJsonOrThrow(r, "That didn't send — mind trying again?");
}

// The admin side: each intake's public path, today's counts and paused state.
export async function listPublicIntakes(app_id) {
  const r = await apiFetch(`/api/apps/${app_id}/public-intakes`, { ...OPTS });
  return jsonOrThrow(r, 'Could not load public forms');
}

export async function resumePublicIntake(app_id, name) {
  const r = await apiFetch(`/api/apps/${app_id}/public-intakes/${encodeURIComponent(name)}/resume`,
    { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not resume that form');
}

// Inbound mail (app admin): each declared mailbox's address, provider URL, today's counts and state; the secret is returned ONCE.
export async function listInboundMail(app_id) {
  const r = await apiFetch(`/api/apps/${app_id}/inbound-mail`, { ...OPTS });
  return jsonOrThrow(r, 'Could not load mailboxes');
}

export async function rotateInboundMailSecret(app_id, name) {
  const r = await apiFetch(`/api/apps/${app_id}/inbound-mail/${encodeURIComponent(name)}/secret`, { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not create a secret for that mailbox');
}

export async function setInboundMailPaused(app_id, name, paused, reason) {
  const r = await apiFetch(`/api/apps/${app_id}/inbound-mail/${encodeURIComponent(name)}/${paused ? 'pause' : 'resume'}`,
    { ...OPTS, method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(reason ? { reason } : {}) });
  return jsonOrThrow(r, paused ? 'Could not pause that mailbox' : 'Could not resume that mailbox');
}
/* A signing link's recipient signs (docs/signature-field-scoping-2026-10.md §3). Same no-cookie posture as the rest of the public pair. A 409
 * carries `body.stale` (the record moved while they read it: the link is still usable), a 400 a sentence of the signer's own to fix. */
export async function signPublicForm(token, body) {
  const r = await fetch(`/api/public/forms/${encodeURIComponent(token)}/sign`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return publicJsonOrThrow(r, "That didn't save — mind trying again?");
}


export async function listInboundMailDeliveries(app_id, name) {
  const r = await apiFetch(`/api/apps/${app_id}/inbound-mail/${encodeURIComponent(name)}/deliveries`, { ...OPTS });
  return jsonOrThrow(r, 'Could not load recent mail');
}

// The issuing side (app admin).
export async function listFormLinks(app_id, all = false) {
  const r = await apiFetch(`/api/apps/${app_id}/form-links${all ? '?all=1' : ''}`, { ...OPTS });
  return jsonOrThrow(r, "Couldn't load this app's form links.");
}

export async function createFormLink(app_id, body) {
  const r = await apiFetch(`/api/apps/${app_id}/form-links`, {
    ...OPTS, method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jsonOrThrow(r, "Couldn't create that form link.");
}

export async function revokeFormLink(app_id, link_id) {
  const r = await apiFetch(`/api/apps/${app_id}/form-links/${link_id}`, {
    ...OPTS, method: 'DELETE',
  });
  return jsonOrThrow(r, "Couldn't revoke that form link.");
}

// --- Migration import (backend/appdata/routes_migration.py) --------------------
// Moving an existing system's data in: a shipped profile (Xero, Salesforce, SAP...)
// names its steps, each loaded from an uploaded file or straight from a connected
// service, into one BATCH that can be reconciled against the source and undone as a
// whole. Every route is org-admin; a non-admin gets 403 and the screen is not offered.
// A profile that does not fit the app answers 422 { profile_errors, config_patch,
// fixable_by_patch } - read it off err.body rather than the message.
export async function migrationProfiles() {
  const r = await apiFetch('/api/migration/profiles', OPTS);
  return jsonOrThrow(r, 'Could not load the import sources'); // { profiles: [{ name, label, kind, connections, params, steps, notes, ... }] }
}
export async function migrationPrepare(appId, name) {
  const r = await apiFetch(`/api/apps/${appId}/migrate/profiles/${encodeURIComponent(name)}/prepare`, OPTS);
  return jsonOrThrow(r, 'Could not check whether this app is ready'); // { needs_config_change, config_patch, patched_config, errors_after_patch }
}
export async function migrationBatches(appId) {
  const r = await apiFetch(`/api/apps/${appId}/migrate/batches`, OPTS);
  return jsonOrThrow(r, 'Could not load earlier migrations'); // { batches: [{ id, source, label, status, created_at, undone_at }] }
}
export async function migrationReport(appId, batchId) {
  const r = await apiFetch(`/api/apps/${appId}/migrate/batches/${encodeURIComponent(batchId)}`, OPTS);
  return jsonOrThrow(r, 'Could not load that report'); // { batch, steps: { [stepId]: {...} }, balanced }
}
// Which app a profile lands in, so a person is never asked "new or existing?" (migration_target.py):
// { mode: 'rerun'|'existing'|'create'|'blocked', app_id?, app_name?, has_data?, blocked?: { reason, apps } }
export async function migrationTarget(name) {
  const r = await apiFetch(`/api/migration/profiles/${encodeURIComponent(name)}/target`, OPTS);
  return jsonOrThrow(r, 'Could not work out where this should go');
}
// Creates the target app with the source-id columns already in place -> { app_id, name }. A 409 carries
// `blocked` or `existing` on err.body when there is already an app to use.
export async function migrationNewApp(name, appName) {
  const r = await apiFetch(`/api/migration/profiles/${encodeURIComponent(name)}/new_app`,
    { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ name: appName }) });
  return jsonOrThrow(r, 'Could not create the app');
}
// Opening receivables/payables against the open documents the migration loaded:
// { match: null | { checks: [{ label, ledger, documents, difference, ok }], ok, causes, acknowledged, skipped_after_cutover } }
export async function migrationMatch(appId, batchId) {
  const r = await apiFetch(`/api/apps/${appId}/migrate/batches/${encodeURIComponent(batchId)}/match`, OPTS);
  return jsonOrThrow(r, 'Could not check the opening balances');
}
export async function migrationMatchAcknowledge(appId, batchId) {
  const r = await apiFetch(`/api/apps/${appId}/migrate/batches/${encodeURIComponent(batchId)}/match/acknowledge`,
    { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: '{}' });
  return jsonOrThrow(r, 'Could not record that');
}
// Before loading: the fiscal period the cutover date falls in and whether the two financial years line up:
// { period: 'open'|'closed'|'missing'|null, source_year_end: {day, month}|null, org_year_start_month, aligned, messages: [{severity, kind, message}] }
export async function migrationCutoverCheck(appId, name, date) {
  const r = await apiFetch(`/api/apps/${appId}/migrate/profiles/${encodeURIComponent(name)}/cutover?date=${encodeURIComponent(date)}`, OPTS);
  return jsonOrThrow(r, 'Could not check that date');
}
// Adds fiscal periods after the latest one (the platform's safe `extend` mode: nothing existing is touched).
export async function migrationExtendPeriods(appId) {
  const r = await apiFetch(`/api/apps/${appId}/migrate/cutover/periods`,
    { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: '{}' });
  return jsonOrThrow(r, 'Could not add fiscal periods');
}
async function migrationPost(appId, path, body, fallback) {
  const r = await apiFetch(`/api/apps/${appId}/migrate/${path}`,
    { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body || {}) });
  return jsonOrThrow(r, fallback);
}
export const migrationCreateBatch = (appId, { profileName, label }) =>
  migrationPost(appId, 'batches', { profile_name: profileName, label }, 'Could not start the migration'); // { batch }
export const migrationPreview = (appId, body) =>
  migrationPost(appId, 'preview', body, 'Could not preview that file');
export const migrationRun = (appId, body) =>
  migrationPost(appId, 'run', body, 'Could not load that file');
export const migrationPull = (appId, body) =>
  migrationPost(appId, 'pull', body, 'Could not pull from the connected service');
export const migrationUndo = (appId, batchId) =>
  migrationPost(appId, `batches/${encodeURIComponent(batchId)}/undo`, {}, 'Could not undo that migration');

// Bank statement import (appdata/routes_bank.py). The file travels as base64 so its bytes reach the parser untouched.
// Gated by the caller's own grants on the bank entities, not org-admin. A load that does not line up with the books
// answers 409 { error } until `acknowledge_findings` is sent; a file that cannot be read answers 422 { error }.
async function bankPost(appId, path, body, fallback) {
  const r = await apiFetch(`/api/apps/${appId}/bank/${path}`,
    { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body || {}) });
  return jsonOrThrow(r, fallback);
}
export const bankSniff = (appId, body) => bankPost(appId, 'sniff', body, 'Could not read that file'); // { delimiter, rows, format }
export const bankPreview = (appId, body) => bankPost(appId, 'preview', body, 'Could not preview that statement');
export const bankLoad = (appId, body) => bankPost(appId, 'load', body, 'Could not load that statement');
export const bankUndo = (appId, batchId) => bankPost(appId, `batches/${encodeURIComponent(batchId)}/undo`, {}, 'Could not undo that import');
// Matching and reconciling (appdata/bank_match.py). A match the entity's own rules refuse answers 422 { error } in plain words.
async function bankGet(appId, path, fallback) {
  const r = await apiFetch(`/api/apps/${appId}/bank/${path}`, OPTS);
  return jsonOrThrow(r, fallback);
}
export const bankSuggestions = (appId, lineId) => bankGet(appId, `lines/${encodeURIComponent(lineId)}/suggestions`, 'Could not look for matches'); // { suggestions: [{ journal_line_id, entry_date, reference, description, amount, days_apart, reference_match }] }
export const bankLineCaptures = (appId, lineId) => bankGet(appId, `lines/${encodeURIComponent(lineId)}/captures`, 'Could not look for receipts'); // { linked: [{ capture_id, supplier_name, total, currency }], suggestions: [{ capture_id, supplier_name, total, currency, days_apart, supplier_match, approximate }], entity }
export const bankMatch = (appId, lineId, journalLineId) => bankPost(appId, `lines/${encodeURIComponent(lineId)}/match`, { journal_line_id: journalLineId }, 'Could not match that line');
// Fill the other side of a bank account's unmatched lines from the bank rules (appdata/bank_rules.py). { applied, skipped_has_match, considered, deferred }
export const bankApplyRules = (appId, bankAccountId) => bankPost(appId, 'rules/apply', { bank_account_id: bankAccountId }, 'Could not apply the bank rules');
// Rules worth making from what was entered by hand (appdata/bank_rules.py): { suggestions: [{ text, counter_account_id, count, direction, tax_rate_id, sample_description }] }
export const bankRuleSuggestions = (appId, bankAccountId) => bankGet(appId, `rules/suggestions?bank_account_id=${encodeURIComponent(bankAccountId)}`, 'Could not look for rule suggestions');
// Suggest the other-side account (and a rule) for an Unmatched line (appdata/bank_suggest.py): history first, the model only when there is none or `askAi`. Writes nothing.
// { line_id, asked_ai, suggestions: [{ account_id, code, name, type, source: 'history'|'ai', reason, count?, tax_rate_id?, unusual?, ai_agrees? }], rule: { match_text, match_kind, match_on, direction, bank_account_id, counter_account_id, tax_rate_id? } | null }
export const bankSuggestCategory = (appId, lineId, askAi = false) => bankPost(appId, 'suggest_category', { line_id: lineId, ask_ai: !!askAi }, 'Could not suggest an account');
export const bankUnmatch = (appId, lineId) => bankPost(appId, `lines/${encodeURIComponent(lineId)}/unmatch`, {}, 'Could not unmatch that line');
export function bankReconciliation(appId, { bankAccountId, asOf, statementBalance }) {
  const q = new URLSearchParams({ bank_account_id: bankAccountId });
  if (asOf) q.set('as_of', asOf);
  if (statementBalance !== undefined && statementBalance !== null && statementBalance !== '') q.set('statement_balance', statementBalance);
  return bankGet(appId, `reconciliation?${q}`, 'Could not load the reconciliation');
}

// --- enterprise sign-in: per-org SAML / OIDC provider, verified domains (enterprise-readiness WP-1.1) ---
// The login page asks this once an email is typed; it never says which org a domain belongs to.
export async function ssoDiscover(email) {
  const r = await apiFetch(`/api/auth/sso/discover?email=${encodeURIComponent(email)}`, OPTS);
  return jsonOrThrow(r, 'Could not check company sign-in'); // { sso, start_url? }
}
const idpJson = (method, path, body) => apiFetch(path, { ...OPTS, method, ...(body ? { headers: JSON_HEADERS, body: JSON.stringify(body) } : {}) });
export async function listIdentityProviders() {
  return jsonOrThrow(await apiFetch('/api/org/identity-providers', OPTS), 'Could not load identity providers'); // { providers }
}
export async function createIdentityProvider(body) {
  return jsonOrThrow(await idpJson('POST', '/api/org/identity-providers', body), 'Could not add the identity provider');
}
export async function updateIdentityProvider(id, patch) {
  return jsonOrThrow(await idpJson('PATCH', `/api/org/identity-providers/${id}`, patch), 'Could not update the identity provider');
}
export async function deleteIdentityProvider(id) {
  return jsonOrThrow(await idpJson('DELETE', `/api/org/identity-providers/${id}`), 'Could not remove the identity provider');
}
export async function testIdentityProvider(id) {
  return jsonOrThrow(await idpJson('POST', `/api/org/identity-providers/${id}/test`), 'Could not test the identity provider');
}
export async function listOrgDomains() {
  return jsonOrThrow(await apiFetch('/api/org/domains', OPTS), 'Could not load domains'); // { domains }
}
export async function addOrgDomain(domain) {
  return jsonOrThrow(await idpJson('POST', '/api/org/domains', { domain }), 'Could not add the domain');
}
export async function verifyOrgDomain(domain) {
  return jsonOrThrow(await idpJson('POST', `/api/org/domains/${encodeURIComponent(domain)}/verify`), 'Could not verify the domain');
}
export async function deleteOrgDomain(domain) {
  return jsonOrThrow(await idpJson('DELETE', `/api/org/domains/${encodeURIComponent(domain)}`), 'Could not remove the domain');
}
export async function setSsoBreakGlass(userId) {
  return jsonOrThrow(await idpJson('PUT', '/api/org/sso-break-glass', { user_id: userId || null }), 'Could not set the break-glass user');
}

// --- My Work (enterprise-readiness WP-17.1) ---------------------------------------
// Everything waiting on the caller across every app. Read-only: acting uses the existing per-kind routes above.
export async function getMyWork() {
  return jsonOrThrow(await apiFetch('/api/me/work', OPTS), 'Could not load your work'); // { items, counts, total }
}

// --- company home (enterprise-front-door WP-21.1) -----------------------------------
export async function getOrgHome() {
  return jsonOrThrow(await apiFetch('/api/org/home', OPTS), 'Could not load the home page'); // { widgets, customised }
}
export async function getHomeConfig() {
  return jsonOrThrow(await apiFetch('/api/org/home-config', OPTS), 'Could not load the home page settings'); // { widgets, customised, types, max_widgets }
}
export async function saveHomeConfig(widgets) {
  const r = await apiFetch('/api/org/home-config', { ...OPTS, method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(widgets === null ? { reset: true } : { widgets }) });
  return jsonOrThrow(r, 'Could not save the home page'); // the server's own sentence on a refusal
}

// --- template upgrades (enterprise-governance WP-13.1) ---------------------------
export async function previewTemplateUpgrade(appId) {
  return jsonOrThrow(await apiFetch(`/api/apps/${appId}/template-upgrade`, OPTS), 'Could not check the template update'); // { conflicts, applied, base_from, ... }
}
export async function fileTemplateUpgrade(appId) {
  const r = await apiFetch(`/api/apps/${appId}/template-upgrade`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: '{}' });
  return jsonOrThrow(r, 'Could not file the template update'); // the proposal, with `upgrade`
}

// --- web push (enterprise-front-door WP-21.2) --------------------------------------
export async function getPushConfig() {
  return jsonOrThrow(await apiFetch('/api/push/config', OPTS), 'Could not check browser notifications'); // { enabled, public_key }
}
export async function subscribePush(subscription) {
  const r = await apiFetch('/api/push/subscribe', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(subscription) });
  return jsonOrThrow(r, 'Could not turn on browser notifications');
}
export async function unsubscribePush(endpoint) {
  const r = await apiFetch('/api/push/subscribe', { ...OPTS, method: 'DELETE', headers: JSON_HEADERS, body: JSON.stringify({ endpoint }) });
  return jsonOrThrow(r, 'Could not turn off browser notifications');
}

// --- "I need..." requests (enterprise-front-door WP-19.1) --------------------------
// The org-wide catalog of request types any member may file, and the caller's own requests. Filing needs no app membership.
export async function listRequestTypes() {
  return jsonOrThrow(await apiFetch('/api/org/request-types', OPTS), 'Could not load the request catalog'); // { types }
}
export async function submitRequest(appId, name, values) {
  const r = await apiFetch(`/api/org/requests/${encodeURIComponent(appId)}/${encodeURIComponent(name)}`,
    { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ values }) });
  return jsonOrThrow(r, 'Could not send the request'); // { id, status }
}
export async function listMyRequests() {
  return jsonOrThrow(await apiFetch('/api/me/requests', OPTS), 'Could not load your requests'); // { requests }
}

// --- search by meaning (enterprise-front-door WP-20.2) ------------------------------
// `status` says whether the box can be used here and, if not, the one reason (off_for_workspace is the org admin's switch).
export async function getSemanticSearchStatus() {
  const r = await apiFetch('/api/org/semantic-search/status', OPTS);
  if (!r.ok) return { available: false, reason: 'error' };
  return r.json().catch(() => ({ available: false, reason: 'error' }));
}
export async function semanticSearch(q) {
  const r = await apiFetch(`/api/org/semantic-search?q=${encodeURIComponent((q || '').trim())}`, OPTS);
  return jsonOrThrow(r, 'Search by meaning isn’t answering right now'); // { results: [{ app_id, app_name, entity, entity_label, id, label, field_label, snippet, score }] }
}
// The business-wide question box (docs/ask-across-the-business-scoping-2026-10.md). A question returns an answer card; a refusal is a 200 with `refused`.
export async function askQuestion(question) {
  const r = await apiFetch('/api/ask', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ question }) });
  return jsonOrThrow(r, 'The question box isn’t answering right now'); // { summary, caveats, table, chart, steps, plan, as_of, apps } | { refused }
}
// "Run again": the plan goes back to be re-validated and re-run as the asker, with no model call and so no prose.
export async function rerunPlan(plan) {
  const r = await apiFetch('/api/ask', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ plan }) });
  return jsonOrThrow(r, 'Could not run that again');
}
// "Propose as a report": files a config PROPOSAL (never a save) adding a single-app, single-step question as a dataset plus a view. Needs config authority.
export async function proposeAskReport(appId, { plan, title, question }) {
  const r = await apiFetch(`/api/apps/${encodeURIComponent(appId)}/ask/propose_report`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ plan, title, question }) });
  return jsonOrThrow(r, 'Could not propose that as a report');
}
export async function reindexSemanticSearch(appId) {
  const r = await apiFetch(`/api/apps/${encodeURIComponent(appId)}/semantic-search/reindex`, { ...OPTS, method: 'POST' });
  return jsonOrThrow(r, 'Could not start indexing'); // { entities }
}

// --- sandboxes and the app inventory (enterprise-governance WP-11.1, WP-11.2) --------
export async function getSandboxInfo(appId) {
  const r = await apiFetch(`/api/apps/${encodeURIComponent(appId)}/sandbox`, OPTS);
  if (!r.ok) return { sandbox: null };
  return r.json().catch(() => ({ sandbox: null })); // { sandbox: null | { of, of_name, expires_at } }
}
export async function listSandboxes(appId) {
  return jsonOrThrow(await apiFetch(`/api/apps/${encodeURIComponent(appId)}/sandboxes`, OPTS), 'Could not load the sandboxes'); // { sandboxes, max, ttl_days }
}
export async function createSandbox(appId, sampleRows = false) {
  const r = await apiFetch(`/api/apps/${encodeURIComponent(appId)}/sandboxes`,
    { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ sample_rows: !!sampleRows }) });
  return jsonOrThrow(r, 'Could not make the sandbox'); // { id, name, expires_at, sample_rows }
}
export async function extendSandbox(appId) {
  return jsonOrThrow(await apiFetch(`/api/apps/${encodeURIComponent(appId)}/sandbox/extend`, { ...OPTS, method: 'POST' }), 'Could not extend the sandbox');
}
// Resolves { ok: true, proposal } | { ok: false, liveChanged: true, message } so the caller can ask before overwriting.
export async function promoteSandbox(appId, acknowledgeOverwrite = false) {
  const r = await apiFetch(`/api/apps/${encodeURIComponent(appId)}/promote`,
    { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ acknowledge_overwrite: !!acknowledgeOverwrite }) });
  const d = await r.json().catch(() => ({}));
  if (r.status === 409 && d.live_changed) return { ok: false, liveChanged: true, message: d.error };
  if (!r.ok) throw apiErrorFrom(r, d, 'Could not promote the sandbox');
  return { ok: true, proposal: d };
}
export async function getAppInventory() {
  return jsonOrThrow(await apiFetch('/api/org/apps/inventory', OPTS), 'Could not load the app inventory'); // { apps }
}
export const APP_INVENTORY_CSV_URL = '/api/org/apps/inventory?format=csv';

// --- personal views and dashboards (enterprise-governance WP-10.2) ----------------
export async function listMyViews() {
  return jsonOrThrow(await apiFetch('/api/me/views', OPTS), 'Could not load your views'); // { views }
}
export async function listDashboards() {
  return jsonOrThrow(await apiFetch('/api/me/dashboards', OPTS), 'Could not load your dashboards'); // { dashboards }
}
export async function createDashboard(name, tiles = []) {
  const r = await apiFetch('/api/me/dashboards', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ name, tiles }) });
  return jsonOrThrow(r, 'Could not create the dashboard');
}
export async function updateDashboard(id, patch) {
  const r = await apiFetch(`/api/me/dashboards/${id}`, { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(patch) });
  return jsonOrThrow(r, 'Could not update the dashboard');
}
export async function deleteDashboard(id) {
  return jsonOrThrow(await apiFetch(`/api/me/dashboards/${id}`, { ...OPTS, method: 'DELETE' }), 'Could not delete the dashboard');
}
export async function getDashboardData(id) {
  return jsonOrThrow(await apiFetch(`/api/me/dashboards/${id}/data`, OPTS), 'Could not load the dashboard'); // { tiles }
}

// --- follow a record, and "notify me when" alerts (enterprise-governance WP-10.3) -----
export async function getFollow(appId, entityKey, recordId) {
  return jsonOrThrow(await apiFetch(`/api/apps/${appId}/records/${entityKey}/${recordId}/follow`, OPTS), 'Could not check following'); // { following }
}
export async function setFollow(appId, entityKey, recordId, on) {
  const r = await apiFetch(`/api/apps/${appId}/records/${entityKey}/${recordId}/follow`, { ...OPTS, method: on ? 'PUT' : 'DELETE' });
  return jsonOrThrow(r, on ? 'Could not follow this record' : 'Could not stop following');
}
export async function createAlert(body) {
  const r = await apiFetch('/api/me/alerts', { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not set up the alert');
}
export async function listAlerts() {
  return jsonOrThrow(await apiFetch('/api/me/alerts', OPTS), 'Could not load your alerts'); // { alerts }
}
export async function deleteAlert(id) {
  return jsonOrThrow(await apiFetch(`/api/me/alerts/${id}`, { ...OPTS, method: 'DELETE' }), 'Could not remove the alert');
}

// --- SCIM provisioning: the org admin's token and group-to-app mappings (enterprise-readiness WP-2.1) ---
export async function getScimOverview() {
  return jsonOrThrow(await apiFetch('/api/org/scim', OPTS), 'Could not load provisioning settings'); // { base_url, tokens, groups, mappings, apps }
}
export async function createScimToken(name) {
  return jsonOrThrow(await idpJson('POST', '/api/org/scim/tokens', { name }), 'Could not create the token'); // { token } is shown once
}
export async function revokeScimToken(id) {
  return jsonOrThrow(await idpJson('DELETE', `/api/org/scim/tokens/${id}`), 'Could not revoke the token');
}
export async function saveScimMapping(body) {
  return jsonOrThrow(await idpJson('POST', '/api/org/scim/group-mappings', body), 'Could not save the mapping');
}
export async function deleteScimMapping(id) {
  return jsonOrThrow(await idpJson('DELETE', `/api/org/scim/group-mappings/${id}`), 'Could not remove the mapping');
}

// --- passkeys (WebAuthn) as a second factor (enterprise-readiness WP-3.2) ----------------
export async function mfaMethods(ticket) {
  return jsonOrThrow(await idpJson('POST', '/api/auth/mfa/methods', { ticket }), 'Could not check sign-in methods'); // { code, totp, passkey }
}
export async function listPasskeys() {
  return jsonOrThrow(await apiFetch('/api/auth/passkeys', OPTS), 'Could not load passkeys'); // { passkeys }
}
export async function passkeyRegisterBegin() {
  return jsonOrThrow(await idpJson('POST', '/api/auth/passkeys/register/begin', {}), 'Could not start adding a passkey'); // { options, ticket }
}
export async function passkeyRegisterFinish(ticket, credential, name) {
  return jsonOrThrow(await idpJson('POST', '/api/auth/passkeys/register/finish', { ticket, credential, name }), 'Could not add the passkey');
}
export async function deletePasskey(id, password) {
  return jsonOrThrow(await idpJson('DELETE', `/api/auth/passkeys/${id}`, { password }), 'Could not remove the passkey');
}
export async function passkeyLoginBegin(ticket) {
  return jsonOrThrow(await idpJson('POST', '/api/auth/passkeys/login/begin', { ticket }), 'Could not start passkey sign-in'); // { options, challenge_ticket }
}
export async function passkeyLoginFinish(ticket, challengeTicket, credential) {
  return jsonOrThrow(await idpJson('POST', '/api/auth/passkeys/login/finish',
    { ticket, challenge_ticket: challengeTicket, credential }), 'That passkey did not work');
}

// --- app groups, group admins and permission sets: the org admin's side (enterprise-readiness WP-3.3, D3) ---
export async function listAppGroups() {
  return jsonOrThrow(await apiFetch('/api/org/app-groups', OPTS), 'Could not load app groups'); // { groups, apps, users }
}
export async function createAppGroup(body) {
  return jsonOrThrow(await idpJson('POST', '/api/org/app-groups', body), 'Could not create the group');
}
export async function updateAppGroup(id, patch) {
  return jsonOrThrow(await idpJson('PATCH', `/api/org/app-groups/${id}`, patch), 'Could not update the group');
}
export async function deleteAppGroup(id) {
  return jsonOrThrow(await idpJson('DELETE', `/api/org/app-groups/${id}`), 'Could not delete the group');
}
export async function listPermissionSets() {
  return jsonOrThrow(await apiFetch('/api/org/permission-sets', OPTS), 'Could not load permission sets'); // { sets, apps, users }
}
export async function createPermissionSet(body) {
  return jsonOrThrow(await idpJson('POST', '/api/org/permission-sets', body), 'Could not create the permission set');
}
export async function updatePermissionSet(id, patch) {
  return jsonOrThrow(await idpJson('PATCH', `/api/org/permission-sets/${id}`, patch), 'Could not update the permission set');
}
export async function deletePermissionSet(id) {
  return jsonOrThrow(await idpJson('DELETE', `/api/org/permission-sets/${id}`), 'Could not delete the permission set');
}
export async function setPermissionSetAssignees(id, userIds) {
  return jsonOrThrow(await idpJson('PUT', `/api/org/permission-sets/${id}/assignees`, { user_ids: userIds }), 'Could not update who holds the set');
}

// --- spreadsheet to app (backend appdata/routes_sheet_imports.py; docs/spreadsheet-to-app-scoping-2026-10.md) ---
// The upload door: the workbook (or CSV) is the raw body, never a model attachment. `appId` uploads a corrected copy of a file that
// app was built from (the import then updates the rows it loaded before, with no new proposal).
export async function uploadSheetImport(file, { appId } = {}) {
  const q = appId ? `?app_id=${encodeURIComponent(appId)}` : '';
  const r = await apiFetch(`/api/sheet-imports${q}`, {
    ...OPTS, method: 'POST',
    headers: { 'Content-Type': 'application/octet-stream', 'X-Filename': encodeURIComponent(file.name || '') },
    body: file,
  });
  return jsonOrThrow(r, 'That file couldn’t be read'); // { id, status, tabs, notes, questions, references, splits, ... }
}
export async function listSheetImports() {
  return jsonOrThrow(await apiFetch('/api/sheet-imports', OPTS), 'Could not load your uploads'); // { imports }
}
export async function getSheetImport(id) {
  return jsonOrThrow(await apiFetch(`/api/sheet-imports/${id}`, OPTS), 'Could not load that upload');
}
export async function proposeSheetImport(id, body = {}) {
  const r = await apiFetch(`/api/sheet-imports/${id}/propose`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not build the app from that file'); // { proposal, import, names }
}
export async function sheetImportReport(id) {
  return jsonOrThrow(await apiFetch(`/api/sheet-imports/${id}/report`, OPTS), 'Could not load the review');
}
export async function previewSheetImport(id, limit = 20) {
  return jsonOrThrow(await apiFetch(`/api/sheet-imports/${id}/preview?limit=${limit}`, OPTS), 'Could not load the preview'); // { tables: [{ entity, tab, columns, rows, rows_total }] }
}
export async function checkSheetImport(id, config) {
  const r = await apiFetch(`/api/sheet-imports/${id}/check`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ config }) });
  return jsonOrThrow(r, 'Could not check the file'); // { will_fail, rows, by_column, notes, findings }
}
export async function setSheetImportChoices(id, choices) {
  const r = await apiFetch(`/api/sheet-imports/${id}/choices`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(choices) });
  return jsonOrThrow(r, 'Could not save that answer'); // the refreshed report
}
export async function startSheetImport(id, body = {}) {
  const r = await apiFetch(`/api/sheet-imports/${id}/import`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
  return jsonOrThrow(r, 'Could not start the import'); // status; a 409 carries body.findings
}
export async function sheetImportStatus(id) {
  return jsonOrThrow(await apiFetch(`/api/sheet-imports/${id}/status`, { ...OPTS, background: true }), 'Could not read the import’s progress');
}
export async function undoSheetImport(id) {
  return jsonOrThrow(await apiFetch(`/api/sheet-imports/${id}/undo`, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: '{}' }), 'Could not undo that import');
}
export async function discardSheetImport(id) {
  return jsonOrThrow(await apiFetch(`/api/sheet-imports/${id}`, { ...OPTS, method: 'DELETE' }), 'Could not discard that upload');
}

// --- accountant access (docs/accountant-access-scoping-2026-10.md) -------------------------------------------------
// A client org invites an accounting practice; the practice accepts, assigns its people, and they open the client's books.
const post = (url, body) => apiFetch(url, { ...OPTS, method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body || {}) });
export async function listPartnerGrants() {
  return jsonOrThrow(await apiFetch('/api/partner/grants', OPTS), 'Could not load accountant access'); // { org_kind, apps, grants }
}
export async function invitePartner(body) {
  return jsonOrThrow(await post('/api/partner/grants', body), 'Could not invite the practice');
}
export async function changePartnerGrant(id, patch) {
  const r = await apiFetch(`/api/partner/grants/${id}`, { ...OPTS, method: 'PATCH', headers: JSON_HEADERS, body: JSON.stringify(patch) });
  return jsonOrThrow(r, 'Could not change that access');
}
export async function revokePartnerGrant(id, reason) {
  return jsonOrThrow(await post(`/api/partner/grants/${id}/revoke`, { reason }), 'Could not end that access');
}
export async function acceptPartnerGrant(id) {
  return jsonOrThrow(await post(`/api/partner/grants/${id}/accept`), 'Could not accept the invitation');
}
export async function assignPartnerPerson(grantId, firmUserId) {
  return jsonOrThrow(await post(`/api/partner/grants/${grantId}/assignments`, { firm_user_id: firmUserId }), 'Could not give them access');
}
export async function unassignPartnerPerson(assignmentId) {
  return jsonOrThrow(await apiFetch(`/api/partner/assignments/${assignmentId}`, { ...OPTS, method: 'DELETE' }), 'Could not remove their access');
}
export async function becomePractice() {
  return jsonOrThrow(await post('/api/partner/practice'), 'Could not set this account up as a practice');
}
export async function listPartnerClients({ status = false, refresh = false } = {}) {
  const q = status ? `?status=1${refresh ? '&refresh=1' : ''}` : '';
  return jsonOrThrow(await apiFetch(`/api/partner/clients${q}`, OPTS), 'Could not load your clients'); // { clients, firm }
}
export async function enterPartnerClient(clientOrgId) {
  return jsonOrThrow(await post(`/api/partner/clients/${clientOrgId}/enter`), 'Could not open that client');
}
export async function backToPractice() {
  return jsonOrThrow(await post('/api/partner/back'), 'Could not go back to your practice');
}
// A practice asks a client for access; the client turns it into an invitation or declines (backend identity/routes_partner.py).
export async function requestPartnerAccess(clientEmail, message) {
  return jsonOrThrow(await post('/api/partner/requests', { client_email: clientEmail, message }), 'Could not send the request');
}
export async function grantPartnerRequest(id, body) {
  return jsonOrThrow(await post(`/api/partner/requests/${id}/grant`, body), 'Could not invite the practice');
}
export async function declinePartnerRequest(id) {
  return jsonOrThrow(await post(`/api/partner/requests/${id}/decline`), 'Could not decline the request');
}
export async function withdrawPartnerRequest(id) {
  return jsonOrThrow(await post(`/api/partner/requests/${id}/withdraw`), 'Could not withdraw the request');
}
// Sign-offs on a record whose entity declares a `signoff` block (backend appdata/routes_signoffs.py).
export async function getSignoffs(appId, entity, recordId) {
  return jsonOrThrow(await apiFetch(`/api/apps/${appId}/signoffs/${entity}/${recordId}`, OPTS), 'Could not load the sign-offs');
}
export async function signOff(appId, entity, recordId, figuresSha256) {
  return jsonOrThrow(await post(`/api/apps/${appId}/signoffs/${entity}/${recordId}`, { figures_sha256: figuresSha256 }), 'Could not sign this off');
}
export async function withdrawSignoff(appId, signoffId, reason) {
  return jsonOrThrow(await post(`/api/apps/${appId}/signoffs/${signoffId}/withdraw`, { reason }), 'Could not withdraw the sign-off');
}
// Step-up: give one's second factor again before something that needs it (backend identity/step_up.py).
export async function stepUpBegin() {
  return jsonOrThrow(await post('/api/auth/step-up/begin'), 'Could not start the check'); // { totp, passkey, options?, challenge_ticket? }
}
export async function stepUp(body) {
  return jsonOrThrow(await post('/api/auth/step-up', body), 'That code did not work. Try again.');
}
export async function getPartnerSession() {
  return jsonOrThrow(await apiFetch('/api/partner/session', OPTS), 'Could not load the session'); // { partner: null | {...} }
}
